(function (root, factory) {
  const api = factory(root, typeof module === 'object' && module.exports ? require('./dim-context-continuation.js') : root.DIMContextContinuation,
    typeof module === 'object' && module.exports ? require('./dim-budget.js') : root.DIMBudget);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DIMDiscovery = api;
}(typeof globalThis !== 'undefined' ? globalThis : undefined, function (root, continuation, budget) {
  'use strict';

  const MAX_WORDING = 3;
  const MAX_WORDING_GRAPHEMES = 120;
  const MAX_FREE_TEXT = 1800;
  const COMPLETED_RESULT_TTL_MS = 7_200_000;
  const MAX_COMPLETED_RECORD_CHARS = 131_072;
  const DEFAULT_OVERLAY_FONT_ID = 'rubik-regular';
  const ILLUSTRATION_INSTRUCTION = 'No words, letters, numbers, captions or typography. Exact wording is added separately in the editor.';
  const COMPLETED_RECORD_KEYS = Object.freeze(['schemaVersion', 'recordScope', 'runtime', 'authority', 'createdAt', 'expiresAt', 'answers', 'wording', 'result', 'candidates']);
  const BRIEF_RECORD_KEYS = Object.freeze(['schemaVersion', 'recordScope', 'runtime', 'authority', 'createdAt', 'expiresAt', 'answers', 'wording', 'editing']);
  const ANSWER_KEYS = Object.freeze(['mode', 'purpose', 'recipient', 'recipientCustom', 'occasion', 'interests', 'budget', 'freeText']);
  const LEGACY_ENTRY_KEYS = Object.freeze(['sourceIntent', 'productConstraint', 'selectedProductId', 'selectedVariantId', 'wordingText', 'wordingOwned', 'photoPresent', 'clarificationIssue', 'clarificationAttempts', 'clarificationQuestion', 'clarificationAnswer', 'clarificationHistory', 'clarificationBrief']);
  const ENTRY_KEYS = Object.freeze([...LEGACY_ENTRY_KEYS, 'entryCreatedAt', 'entryRevision']);
  const CORRECTION_ISSUES = Object.freeze(['purpose', 'source', 'product', 'wording', 'budget']);
  const confirmedIssues = raw => String(raw || '').split(',').filter(issue => CORRECTION_ISSUES.includes(issue));
  const normalizedBrief = value => String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-GB');
  const answerShape = value => exactObjectKeys(value, ANSWER_KEYS) || [LEGACY_ENTRY_KEYS, ENTRY_KEYS].some(keys =>
    exactObjectKeys(value, [...ANSWER_KEYS, ...keys]) || exactObjectKeys(value, [...ANSWER_KEYS, ...keys, 'confirmedClarifications']));
  const ordinaryCapability = runtime => runtime?.capabilities?.includes?.('ordinary-language-intake@1') === true;
  const reuseCapability = runtime => runtime?.capabilities?.includes?.('journey-data-reuse@1') === true;
  const refreshableAuthority = code => ['JOURNEY_AUTHORITY_EXPIRED', 'JOURNEY_AUTHORITY_MISSING', 'JOURNEY_REVISION_CHANGED', 'JOURNEY_SOURCE_CHANGED', 'STALE_CONTEXT', 'CONTEXT_EXPIRED'].includes(code);
  const usesOriginalPhoto = intake => intake?.photoPresent === true && !['create', 'words'].includes(intake.sourceIntent);
  const PHOTO_EXPLANATION = 'Your photo stays as supplied. You can add words and adjust its layout; changing people, objects or the background is not supported.';
  const supportedPhotoText = (value, rawText, maximum, ignoreCase = false, minimum = 1) => {
    if (typeof value !== 'string' || value.length < minimum || value.length > maximum || value !== value.trim() || typeof rawText !== 'string') return null;
    return (ignoreCase ? rawText.toLocaleLowerCase('en-GB').includes(value.toLocaleLowerCase('en-GB')) : rawText.includes(value)) ? value : null;
  };
  function projectPhotoUnderstanding(understood, intake) {
    if (!usesOriginalPhoto(intake)) return understood;
    return { ...understood, sourceIntent: 'upload', purpose: intake.purpose ?? null, recipientRelationship: intake.recipientRelationship ?? null,
      interests: intake.interests ?? null, productConstraint: intake.productConstraint ?? null, selectedProduct: intake.selectedProduct ?? null,
      wording: intake.wording != null || intake.confirmedClarifications?.includes('wording') ? intake.wording : supportedPhotoText(understood?.wording, intake.rawText, 1000),
      occasion: intake.occasion ?? supportedPhotoText(understood?.occasion, intake.rawText, 100, true, 2) };
  }
  function projectPhotoRecommendation(idea, intake) {
    if (!usesOriginalPhoto(intake)) return idea;
    return { ...idea, title: 'Your photo and words', reason: 'Use your own photo with optional words and layout.',
      designSummary: 'Your original photo, with optional independent words and layout.',
      artworkIntent: { subject: 'The original customer photo, used as supplied.', suggestedStyle: idea?.artworkIntent?.suggestedStyle || 'natural',
        compositionIntent: 'Keep the photo unchanged; adjust only its placement and independent words.',
        wordingSuggestion: typeof idea?.artworkIntent?.wordingSuggestion === 'string' && idea.artworkIntent.wordingSuggestion.length <= 160 ? idea.artworkIntent.wordingSuggestion : null, letteringMode: 'none' },
      photoCapability: { mode: 'original-photo', explanation: PHOTO_EXPLANATION, originalRequest: typeof intake.rawText === 'string' ? intake.rawText : null } };
  }
  function clarificationProgress(previous, detail) {
    const issues = ['purpose', 'recipient', 'interests', 'product', 'source', 'wording', 'budget', 'brief'];
    const issue = issues.includes(detail?.issue) ? detail.issue : 'brief';
    const attempts = previous?.issue === issue ? Math.min(2, Number(previous.attempts || 0) + 1) : 0;
    return { issue, attempts, stopped: attempts >= 2, question: String(detail?.question || '').slice(0, 280) };
  }
  const AUTHORITY_KEYS = Object.freeze(['schemaVersion', 'storefrontId', 'storefrontRevision', 'bindingId', 'mode', 'purpose', 'catalogueRevision', 'activationEpoch']);
  const REJECTION_STATUS = Object.freeze({ TURNSTILE_INVALID: 403, BUDGET_CLARIFICATION_REQUIRED: 400,
    STALE_CONTEXT: 409, INCOMPLETE_BRIEF: 400, INVALID_INTAKE: 400, INVALID_FIELDS: 400, INVALID_TEXT: 400,
    INVALID_INTEGER: 400, INVALID_BUDGET: 400, RATE_LIMITED: 429, ADMISSION_UNAVAILABLE: 503,
    RECOMMENDATION_UNAVAILABLE: 503, CONTEXT_MISMATCH: 409, CONTEXT_EXPIRED: 410,
    STAFF_PREVIEW_DENIED: 409, STAFF_PREVIEW_CONTINUATION_UNSUPPORTED: 409, STAFF_PREVIEW_UNAVAILABLE: 503,
    DEV_ACCESS_UNAVAILABLE: 503, DEV_ACCESS_DENIED: 403,
    JOURNEY_AUTHORITY_EXPIRED: 409, JOURNEY_AUTHORITY_MISSING: 404, JOURNEY_REVISION_CHANGED: 409,
    JOURNEY_SOURCE_CHANGED: 409, JOURNEY_REVOKED: 403, JOURNEY_AUTHORITY_INVALID: 401 });
  const unavailableIdeasMessage = 'That request completed, but its suggestions could not be recovered. Your answers are still here. Select Find suggestions to request new suggestions.';
  const rejectionMessage = (code) => code === 'TURNSTILE_INVALID'
    ? 'The security check expired or was not accepted. Complete it again, then select Find suggestions. Your request was not sent for suggestions.'
    : code === 'RATE_LIMITED' ? 'Please wait before requesting more suggestions. This request was not sent for suggestions.'
      : code === 'STALE_CONTEXT' || code === 'CONTEXT_MISMATCH' || code === 'CONTEXT_EXPIRED'
        ? 'The available products have changed. Reload this page before requesting more suggestions. This request was not sent for suggestions.'
        : code === 'BUDGET_CLARIFICATION_REQUIRED' ? 'Check your maximum budget for the product itself, excluding delivery, then select Find suggestions.'
          : 'That request was not sent for suggestions. Your answers are still here. You can select Find suggestions to try again.';
  const identity = (value) => JSON.stringify(value || {});
  const trimOrNull = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };
  const graphemes = (value) => {
    const text = String(value || '');
    if (typeof Intl !== 'undefined' && Intl.Segmenter) return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)).length;
    return Array.from(text).length;
  };
  const approvedFonts = (manifest) => manifest && manifest.schemaVersion === 'dim-font-manifest@1' && manifest.approval?.status === 'approved'
    && manifest.fonts && typeof manifest.fonts === 'object' && !Array.isArray(manifest.fonts)
    && manifest.fonts[DEFAULT_OVERLAY_FONT_ID] && trimOrNull(manifest.fonts[DEFAULT_OVERLAY_FONT_ID].revision)
    ? manifest.fonts : null;
  const positiveBudget = (value) => {
    const text = String(value || '').trim();
    if (!text) return null;
    if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error('INVALID_BUDGET');
    const amount = Number(text);
    if (!Number.isFinite(amount) || amount < 0 || amount > 100000) throw new Error('INVALID_BUDGET');
    return amount;
  };
  const wholePoundEntry = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return null;
    if (!/^[0-9]+$/.test(text)) throw new Error('INVALID_BUDGET');
    return positiveBudget(text);
  };

  function validateDiscoveryStep(_stateValue, answers = {}) {
    const mode = answers.mode === 'free_text' ? 'free_text' : 'guided';
    if (mode === 'guided') {
      if (answers.purpose !== 'gift' && answers.purpose !== 'self') {
        return { code: 'PURPOSE_REQUIRED', message: 'Choose who you are making this for.', field: 'purpose' };
      }
      if (answers.purpose === 'gift' && (!trimOrNull(answers.recipientCustom)
        || graphemes(answers.recipientCustom) > 80)) {
        return { code: 'RECIPIENT_REQUIRED', message: 'Tell us who this is for, in up to 80 characters.', field: 'recipient_custom' };
      }
      if (!trimOrNull(answers.interests) || graphemes(answers.interests) > 240) {
        return { code: 'INTERESTS_REQUIRED', message: 'Tell us a little more, in up to 240 characters.', field: 'interests' };
      }
      if (graphemes(answers.occasion) > 80) return { code: 'OCCASION_TOO_LONG', message: 'Keep the occasion to 80 characters or fewer.', field: 'occasion' };
    } else if ((!trimOrNull(answers.freeText) && answers.photoPresent !== 'true' && !['upload', 'create', 'words'].includes(answers.sourceIntent)) || graphemes(answers.freeText) > MAX_FREE_TEXT) {
      return { code: 'INVALID_TEXT', message: graphemes(answers.freeText) > MAX_FREE_TEXT ? 'Keep your idea within 1800 characters.' : 'Add an idea, or choose one of the routes below to get started.', field: 'free_text' };
    }
    try { wholePoundEntry(answers.budget); } catch (_) {
      return { code: 'INVALID_BUDGET', message: 'Enter a whole number, such as 25.', field: 'budget' };
    }
    return null;
  }

  const RECIPIENT_PRESETS = Object.freeze({ partner: 'My partner', parent: 'A parent', grandparent: 'A grandparent', friend: 'A friend' });
  const recipientInput = (answers) => answers.recipient === 'someone-else' ? answers.recipientCustom : answers.recipient;
  const intakeVisibility = ({ purpose, recipientCustom, interests }) => {
    const gift = purpose === 'gift'; const self = purpose === 'self';
    const details = self || gift && Boolean(trimOrNull(recipientCustom));
    return { recipient: gift, details, submission: details && Boolean(trimOrNull(interests)) };
  };

  function parseIntake(raw) {
    const mode = raw && raw.mode === 'free_text' ? 'free_text' : 'guided';
    const purpose = raw && raw.purpose === 'self' ? 'self' : 'gift';
    const rawText = trimOrNull(raw && raw.freeText);
    if (mode === 'free_text') {
      const extended = Object.hasOwn(raw || {}, 'sourceIntent');
      if ((!rawText && !(extended && (raw.photoPresent === 'true' || raw.photoPresent === true || ['upload', 'create', 'words'].includes(raw.sourceIntent)))) || graphemes(rawText) > MAX_FREE_TEXT) throw new Error('INVALID_TEXT');
      return {
        entryMode: 'free_text', purpose: extended && ['self', 'gift'].includes(raw.purpose) ? raw.purpose : null, recipientRelationship: null, occasion: null,
        interests: null, budgetGbp: positiveBudget(raw && raw.budget), rawText: extended && trimOrNull(raw.clarificationHistory) ? `${rawText || ''}\n${raw.clarificationHistory}`.trim() : rawText,
        ...(extended ? { sourceIntent: ['upload', 'create', 'words'].includes(raw.sourceIntent) ? raw.sourceIntent : 'unknown',
          productConstraint: trimOrNull(raw.productConstraint), selectedProduct: trimOrNull(raw.selectedProductId) ? { productId: String(raw.selectedProductId), variantId: trimOrNull(raw.selectedVariantId) } : null,
          wording: trimOrNull(raw.wordingText), photoPresent: raw.photoPresent === true || raw.photoPresent === 'true' } : {}),
        ...(extended && confirmedIssues(raw.confirmedClarifications).length ? { confirmedClarifications: confirmedIssues(raw.confirmedClarifications) } : {}),
      };
    }
    const interests = trimOrNull(raw && raw.interests);
    if (!interests) throw new Error('INTERESTS_REQUIRED');
    if (purpose === 'gift' && !trimOrNull(raw && raw.recipient)) throw new Error('RECIPIENT_REQUIRED');
    return {
      entryMode: 'guided', purpose, recipientRelationship: purpose === 'self' ? null : trimOrNull(raw && raw.recipient),
      occasion: trimOrNull(raw && raw.occasion), interests, budgetGbp: positiveBudget(raw && raw.budget), rawText: null,
    };
  }

  function minimumEligiblePrice(candidates) {
    const prices = [];
    for (const candidate of candidates || []) {
      if (Array.isArray(candidate.allowedSelections)) {
        for (const variant of candidate.variants || []) {
          if (candidate.allowedSelections.some((selection) => String(selection.productId) === String(candidate.productId)
            && String(selection.variantId) === String(variant.variantId))
            && variant.currency === 'GBP' && Number.isInteger(variant.priceMinor) && variant.priceMinor >= 0) prices.push(variant.priceMinor);
        }
      } else if (candidate.currency === 'GBP' && Number.isInteger(candidate.priceMinor) && candidate.priceMinor >= 0) prices.push(candidate.priceMinor);
    }
    return prices.length ? Math.min(...prices) : null;
  }

  function createChallengeController(api, widget, onReady = () => {}, onProblem = () => {}) {
    let widgetId = null, token = null, mounted = false, disposed = false;
    const renew = () => {
      token = null;
      if (disposed) return;
      if (mounted && widgetId !== null && widgetId !== undefined) {
        try { api.reset(widgetId); } catch (_) { /* remain tokenless; no request is resent */ }
      }
      onReady();
    };
    return Object.freeze({
      mount() {
        if (disposed || mounted || typeof api?.render !== 'function') return;
        widgetId = api.render(widget, { callback(value) { if (!disposed) { token = trimOrNull(value); if (token) onReady(); } },
          'expired-callback': () => { if (!disposed) { renew(); onProblem('expired'); } },
          'error-callback': () => { if (!disposed) { renew(); onProblem('error'); } },
          'timeout-callback': () => { if (!disposed) { renew(); onProblem('timeout'); } } });
        mounted = true;
      },
      peek() { return token; },
      take() { const value = token; renew(); return value; },
      renew,
      dispose() { disposed = true; token = null; if (mounted && widgetId !== null && widgetId !== undefined) {
        try { api.remove?.(widgetId); } catch (_) { /* retired callbacks remain inert if the widget was already removed */ }
      } mounted = false; },
    });
  }

  function preflight({ intake, candidates }) {
    const available = Array.isArray(candidates) ? candidates : [];
    if (!budget || budget.schemaVersion !== 'dim-budget@1' || typeof budget.reconcileBudget !== 'function') throw new Error('BUDGET_ADAPTER_UNAVAILABLE');
    let policy;
    try { policy = budget.reconcileBudget(intake.confirmedClarifications?.includes('budget') ? { ...intake, rawText: null } : intake); } catch (error) {
      return { status: 'clarification', code: error?.message || 'INVALID_BUDGET', message: 'Enter a whole number, such as 25.' };
    }
    if (policy.status === 'clarification') {
      return { status: 'clarification', code: policy.basis, message: 'What is your maximum budget for the product itself, excluding delivery?' };
    }
    if (policy.maxMinor !== null) {
      const limit = policy.maxMinor;
      const minimum = minimumEligiblePrice(available);
      if (minimum === null || minimum > limit) {
        return { status: 'clarification', code: 'NO_CANDIDATE_WITHIN_BUDGET', message: minimum === null ? 'No available suggestions are within that budget. Increase it or continue without a budget.' : `The lowest-priced available product is ${formatGbp(minimum)}. Increase your budget or leave it blank to see all suggestions.` };
      }
    }
    return { status: 'ready' };
  }

  function createOperation({ runtime, context, requestId, plannerRequestId = null, intake = null, answers = null, wording = null, journeyAuthority = null, authorityGeneration = null }) {
    if (!requestId || !runtime || !context) throw new Error('INVALID_OPERATION');
    continuation.validateContext(context);
    if (plannerRequestId !== null && !trimOrNull(plannerRequestId)) throw new Error('INVALID_OPERATION');
    const answerValue = answers === null ? null : validAnswers(answers); const wordingValue = wording === null ? null : validWording(wording);
    if (answers !== null && !answerValue || wording !== null && !wordingValue) throw new Error('INVALID_OPERATION');
    return Object.freeze({ requestId: String(requestId), plannerRequestId: plannerRequestId === null ? null : String(plannerRequestId),
      intake: intake ? JSON.parse(JSON.stringify(intake)) : null, answers: answerValue?.answers || null, wording: wordingValue,
      runtime: identity(runtime), context: identity(context), ...(journeyAuthority ? { journeyAuthority: JSON.parse(JSON.stringify(journeyAuthority)),
        ...(Number.isSafeInteger(authorityGeneration) ? { authorityGeneration } : {}) } : {}) });
  }

  function canCommitOperation(operation, candidate) {
    try {
      return Boolean(operation && candidate && operation.requestId === String(candidate.requestId)
        && operation.runtime === identity(candidate.runtime) && sameContext(JSON.parse(operation.context), candidate.context));
    } catch (_) { return false; }
  }

  function createRecommendationInput({ intake, context, draftRevision = 0, requestId: clientRequestId }) {
    if (!intake || !context || !trimOrNull(clientRequestId) || !Number.isSafeInteger(draftRevision) || draftRevision < 0
      || context.schemaVersion !== 'storefront-context@1' || !trimOrNull(context.storefrontId) || !trimOrNull(context.storefrontRevision)) throw new Error('INVALID_INTAKE');
    const extended = Object.hasOwn(intake, 'sourceIntent');
    return Object.freeze({ schemaVersion: extended ? 'dream-intake@2' : 'dream-intake@1', requestId: String(clientRequestId), draftRevision,
      entryMode: intake.entryMode, purpose: intake.purpose, recipientRelationship: intake.recipientRelationship, occasion: intake.occasion,
      interests: intake.interests, budgetGbp: intake.budgetGbp, rawText: intake.rawText, storefrontContextRef: context.storefrontId,
      expectedStorefrontRevision: context.storefrontRevision, locale: 'en-GB',
      ...(extended ? { sourceIntent: intake.sourceIntent, productConstraint: intake.productConstraint, selectedProduct: intake.selectedProduct, wording: intake.wording, photoPresent: intake.photoPresent,
        ...(intake.confirmedClarifications ? { confirmedClarifications: [...intake.confirmedClarifications] } : {}) } : {}) });
  }

  const sameContext = (left, right) => continuation.sameAuthorityIdentity(left, right);

  function restoreOperation(raw, { runtime, context, recordScope }) {
    try {
      const record = JSON.parse(raw);
      if (record?.schemaVersion !== 'dim-recommendation-pending@1' || record.recordScope !== recordScope || !record.operation
        || !canCommitOperation(record.operation, { runtime, context, requestId: record.operation.requestId })) return null;
      return Object.freeze({ ...record.operation });
    } catch (_) { return null; }
  }

  function selectExactCandidate(candidates, idea) {
    const candidate = Array.isArray(candidates) && candidates.find((value) => value.candidateRef === idea?.candidateRef);
    if (!candidate || !Array.isArray(candidate.allowedSelections)) return null;
    return candidate.allowedSelections.find((value) => value.productId === idea.selection?.productId && value.variantId === idea.selection?.variantId
      && identity(value.options || []) === identity(idea.selection?.options || [])) || null;
  }

  function constrainCandidates(candidates, selector) {
    if (!selector) return candidates;
    return candidates.filter(candidate => candidate.productId === selector.productId).flatMap(candidate => {
      if (selector.variantId === null) return [candidate];
      const variants = candidate.variants.filter(variant => variant.variantId === selector.variantId);
      const allowedSelections = candidate.allowedSelections.filter(selection => selection.productId === selector.productId && selection.variantId === selector.variantId);
      return variants.length && allowedSelections.length ? [{ ...candidate, variants, allowedSelections, selectedVariantId: selector.variantId, selectionRequired: false }] : [];
    });
  }

  function optionChoices(candidate) {
    const allowed = Array.isArray(candidate?.allowedSelections) ? candidate.allowedSelections : [];
    const byName = new Map();
    for (const selection of allowed) {
      for (const option of selection.options || []) {
        if (!byName.has(option.name)) byName.set(option.name, new Set());
        byName.get(option.name).add(option.value);
      }
    }
    return [...byName.entries()].map(([name, values]) => ({ name, values: [...values].sort() }));
  }

  const validWordsCapability = value => {
    try { return Boolean(value && root.DreamItArtworkProfiles?.validateWordsOnlyCapability?.(value)); } catch (_) { return false; }
  };
  function wordsCandidates(candidates) {
    return (candidates || []).flatMap(candidate => {
      const variants = (candidate.variants || []).filter(variant => validWordsCapability(variant.wordsOnly));
      const allowedSelections = (candidate.allowedSelections || []).filter(selection => variants.some(variant => variant.variantId === selection.variantId));
      if (!variants.length || !allowedSelections.length || !candidate.selectionRequired && !allowedSelections.some(selection => selection.variantId === candidate.selectedVariantId)) return [];
      return [{ ...candidate, variants, allowedSelections }];
    });
  }

  function exactSelectionForChoices(candidate, choices) {
    const selected = optionChoices(candidate).map(option => ({ name: option.name,
      value: choices?.[option.name] || (option.values.length === 1 ? option.values[0] : '') }));
    if (!selected.length && candidate?.allowedSelections?.length === 1) return candidate.allowedSelections[0];
    if (!selected.length || selected.some((option) => !trimOrNull(option.value))) return null;
    return (candidate?.allowedSelections || []).find((selection) => identity(selection.options || []) === identity(selected)) || null;
  }

  function createCoordinator() {
    let current = { status: 'idle', requestId: null };
    return Object.freeze({
      begin(requestId) {
        if (current.status !== 'idle') return false;
        current = { status: 'pending', requestId: String(requestId) };
        return true;
      },
      finish(requestId, outcome) {
        if (current.requestId !== String(requestId)) return { status: 'ignored' };
        if (outcome === 'uncertain') {
          current = { status: 'uncertain', requestId: current.requestId };
          return { status: 'uncertain', retry: 'manual_only' };
        }
        current = { status: 'idle', requestId: null };
        return { status: 'idle' };
      },
      reset(requestId) {
        if (current.status !== 'uncertain' || current.requestId !== String(requestId)) return false;
        current = { status: 'idle', requestId: null };
        return true;
      },
      state: () => ({ ...current }),
    });
  }

  function classifyRequestOutcome(response, payload, expectedRequestId) {
    const uncertain = Object.freeze({ status: 'uncertain' });
    if (!response || !Number.isSafeInteger(response.status) || !payload || typeof payload !== 'object' || Array.isArray(payload)
      || payload.requestToken !== String(expectedRequestId)) return uncertain;
    const exactKeys = keys => JSON.stringify(Object.keys(payload).sort()) === JSON.stringify([...keys].sort());
    if (response.status === 202 && response.ok === true && payload.status === 'pending'
      && exactKeys(['status', 'requestToken'])) return Object.freeze({ status: 'pending' });
    if (response.status === 200 && response.ok === true && payload.status === 'completed'
      && (exactKeys(['status', 'requestToken']) || exactKeys(['status', 'requestToken', 'result']))) {
      return Object.freeze({ status: 'completed', result: Object.hasOwn(payload, 'result') ? payload.result : null });
    }
    if (response.status === 409 && response.ok === false && payload.explicitRetryRequired === true
      && exactKeys(['status', 'requestToken', 'explicitRetryRequired'])) {
      if (payload.status === 'completed_unavailable') return Object.freeze({ status: 'completed', result: null });
      if (payload.status === 'not_dispatched' || payload.status === 'rejected') return Object.freeze({ status: payload.status });
    }
    if (response.ok === false && payload.status === 'rejected' && payload.explicitRetryRequired === true
      && exactKeys(['status', 'requestToken', 'explicitRetryRequired', 'code'])
      && Object.hasOwn(REJECTION_STATUS, payload.code) && REJECTION_STATUS[payload.code] === response.status) {
      return Object.freeze({ status: 'rejected', code: payload.code });
    }
    return uncertain;
  }

  const exactObjectKeys = (value, keys) => Boolean(value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key)));
  const runtimeIdentity = (value) => {
    try {
      if (!value || !['development', 'production'].includes(value.environment) || !trimOrNull(value.instanceId)
        || !trimOrNull(value.themeId) || !trimOrNull(value.workerOrigin)) return null;
      const worker = new URL(value.workerOrigin);
      if (worker.protocol !== 'https:' || worker.origin !== value.workerOrigin || worker.username || worker.password || worker.hash) return null;
      return JSON.stringify([value.environment, value.instanceId, value.environment === 'development' ? value.themeId : null, worker.origin]);
    } catch (_) { return null; }
  };
  const authorityIdentity = (value) => {
    try {
      continuation.validateContext(value);
      return Object.freeze(Object.fromEntries(AUTHORITY_KEYS.map(key => [key, value[key]])));
    } catch (_) { return null; }
  };
  const validAnswers = (value) => {
    if (!answerShape(value) || Object.keys(value).some(key => typeof value[key] !== 'string' || value[key].length > MAX_FREE_TEXT)) return null;
    try {
      const intake = parseIntake({ ...value, recipient: recipientInput(value) });
      return { answers: JSON.parse(JSON.stringify(value)), intake };
    } catch (_) { return null; }
  };
  const validWording = (value) => Array.isArray(value) && value.length <= MAX_WORDING
    && value.every(item => typeof item === 'string' && graphemes(item) <= MAX_WORDING_GRAPHEMES)
    ? JSON.parse(JSON.stringify(value)) : null;

  // Unsent and edited answers may deliberately be incomplete or invalid. They
  // are retained separately from the immutable answers that produced ideas.
  function createBriefRecord({ runtime, context, recordScope, answers, wording, editing = false,
    nowMs = Date.now(), expiresAt = nowMs + COMPLETED_RESULT_TTL_MS }) {
    const runtimeValue = runtimeIdentity(runtime); const authority = authorityIdentity(context);
    if (!runtimeValue || !authority || typeof recordScope !== 'string' || !recordScope
      || !answerShape(answers) || Object.keys(answers).some(key => typeof answers[key] !== 'string' || answers[key].length > 4000)
      || !validWording(wording) || typeof editing !== 'boolean' || !Number.isSafeInteger(nowMs) || nowMs < 0
      || !Number.isSafeInteger(expiresAt) || expiresAt <= nowMs || expiresAt > nowMs + COMPLETED_RESULT_TTL_MS) throw new Error('INVALID_BRIEF_RECORD');
    return Object.freeze({ schemaVersion: 'dim-recommendation-brief@1', recordScope, runtime: runtimeValue, authority,
      createdAt: expiresAt - COMPLETED_RESULT_TTL_MS, expiresAt, answers: JSON.parse(JSON.stringify(answers)), wording: validWording(wording), editing });
  }

  function restoreBriefRecord(raw, { runtime, context, recordScope, nowMs = Date.now() }) {
    try {
      if (typeof raw !== 'string' || raw.length > 40_000) return null;
      const record = JSON.parse(raw); const authority = authorityIdentity(context);
      if (!exactObjectKeys(record, BRIEF_RECORD_KEYS) || record.schemaVersion !== 'dim-recommendation-brief@1'
        || record.runtime !== runtimeIdentity(runtime) || record.recordScope !== recordScope || !authority
        || !exactObjectKeys(record.authority, AUTHORITY_KEYS) || AUTHORITY_KEYS.some(key => record.authority[key] !== authority[key])
        || record.createdAt !== record.expiresAt - COMPLETED_RESULT_TTL_MS || record.createdAt > nowMs) return null;
      return createBriefRecord({ runtime, context, recordScope, answers: record.answers, wording: record.wording,
        editing: record.editing, expiresAt: record.expiresAt, nowMs });
    } catch (_) { return null; }
  }
  const safeResult = (value, { includeContext }) => {
    const keys = ['status', 'requestId', 'draftRevision', ...(includeContext ? ['context'] : []), 'understood', 'clarification', 'ideas'];
    if (!(exactObjectKeys(value, keys) || exactObjectKeys(value, [...keys, 'clarificationDetail'])) || value.status !== 'ideas' || !trimOrNull(value.requestId)
      || !Number.isSafeInteger(value.draftRevision) || value.draftRevision < 0 || !value.understood || typeof value.understood !== 'object'
      || !Array.isArray(value.ideas) || !value.ideas.length || value.ideas.length > 3) return null;
    if (includeContext && !authorityIdentity(value.context)) return null;
    const copy = JSON.parse(JSON.stringify(value));
    if (includeContext) delete copy.context;
    return copy;
  };

  function createCompletedResultRecord({ runtime, context, recordScope, answers, wording, result, candidates, journeyAuthority = null, authorityGeneration = null, nowMs = Date.now() }) {
    const runtimeValue = runtimeIdentity(runtime); const authority = authorityIdentity(context);
    const answerValue = validAnswers(answers); const wordingValue = validWording(wording); const resultValue = safeResult(result, { includeContext: true });
    const referenced = new Set(resultValue?.ideas.map(idea => idea?.candidateRef));
    const candidateValue = JSON.parse(JSON.stringify((candidates || []).filter(candidate => referenced.has(candidate?.candidateRef))));
    if (!runtimeValue || !authority || typeof recordScope !== 'string' || !recordScope || !answerValue || !wordingValue || !resultValue
      || !Number.isSafeInteger(nowMs) || nowMs < 0 || !candidateValue.length || candidateValue.length > 3 || candidateValue.length !== referenced.size
      || exactCandidates(candidateValue).length !== candidateValue.length
      || !sameContext(result.context, context)) throw new Error('INVALID_COMPLETED_RESULT');
    const record = { schemaVersion: 'dim-recommendation-completed@1', recordScope, runtime: runtimeValue, authority,
      createdAt: nowMs, expiresAt: nowMs + COMPLETED_RESULT_TTL_MS, answers: answerValue.answers, wording: wordingValue,
      result: resultValue, candidates: candidateValue, ...(journeyAuthority ? { journeyAuthority: JSON.parse(JSON.stringify(journeyAuthority)),
        ...(Number.isSafeInteger(authorityGeneration) ? { authorityGeneration } : {}) } : {}) };
    if (JSON.stringify(record).length > MAX_COMPLETED_RECORD_CHARS) throw new Error('INVALID_COMPLETED_RESULT');
    return Object.freeze(record);
  }

  function restoreCompletedResult(raw, { runtime, context, recordScope, nowMs = Date.now() }) {
    try {
      if (typeof raw !== 'string' || raw.length > MAX_COMPLETED_RECORD_CHARS) return null;
      const record = JSON.parse(raw);
      const runtimeValue = runtimeIdentity(runtime); const authority = authorityIdentity(context);
      if (!(exactObjectKeys(record, COMPLETED_RECORD_KEYS) || (exactObjectKeys(record, [...COMPLETED_RECORD_KEYS, 'journeyAuthority'])
          || exactObjectKeys(record, [...COMPLETED_RECORD_KEYS, 'journeyAuthority', 'authorityGeneration']) && Number.isSafeInteger(record.authorityGeneration) && record.authorityGeneration >= 0)
          && exactObjectKeys(record.journeyAuthority, ['schemaVersion', 'reference', 'expiresAt'])
          && record.journeyAuthority.schemaVersion === 'journey-authority@1' && trimOrNull(record.journeyAuthority.reference)
          && Number.isSafeInteger(record.journeyAuthority.expiresAt)) || record.schemaVersion !== 'dim-recommendation-completed@1'
        || record.recordScope !== recordScope || record.runtime !== runtimeValue || !runtimeValue || !authority
        || !exactObjectKeys(record.authority, AUTHORITY_KEYS) || AUTHORITY_KEYS.some(key => record.authority[key] !== authority[key]) || !Number.isSafeInteger(nowMs)
        || !Number.isSafeInteger(record.createdAt) || record.createdAt < 0 || record.createdAt > nowMs
        || record.expiresAt !== record.createdAt + COMPLETED_RESULT_TTL_MS || record.expiresAt <= nowMs) return null;
      const answerValue = validAnswers(record.answers); const wordingValue = validWording(record.wording);
      const resultValue = safeResult(record.result, { includeContext: false });
      const candidateValue = JSON.parse(JSON.stringify(record.candidates || []));
      const referenced = new Set(resultValue?.ideas.map(idea => idea?.candidateRef));
      if (!answerValue || !wordingValue || !resultValue || !candidateValue.length || candidateValue.length > 3 || candidateValue.length !== referenced.size
        || candidateValue.some(candidate => !referenced.has(candidate?.candidateRef)) || exactCandidates(candidateValue).length !== candidateValue.length) return null;
      return Object.freeze({ answers: answerValue.answers, intake: answerValue.intake, wording: wordingValue,
        result: Object.freeze({ ...resultValue, context: JSON.parse(JSON.stringify(context)) }), candidates: candidateValue,
        createdAt: record.createdAt, expiresAt: record.expiresAt, ...(record.journeyAuthority ? { journeyAuthority: record.journeyAuthority,
          ...(Number.isSafeInteger(record.authorityGeneration) ? { authorityGeneration: record.authorityGeneration } : {}) } : {}) });
    } catch (_) { return null; }
  }

  function readSuggestedWording(raw, expected, design, textModel) {
    try {
      if (!expected || !design?.selection || !textModel?.validateLayers) return '';
      const restored = restoreCompletedResult(raw, expected);
      if (!restored) return '';
      const brief = JSON.parse(design.creativeBrief);
      if (!brief || typeof brief !== 'object' || !trimOrNull(brief.subject) || !trimOrNull(brief.style)
        || !trimOrNull(brief.compositionIntent)
        || brief.letteringInstruction !== ILLUSTRATION_INSTRUCTION) return '';
      const selected = design.selection;
      const matches = restored.result.ideas.filter((idea) => {
        const candidate = restored.candidates.find((item) => item.candidateRef === idea?.candidateRef);
        if (!candidate || !Array.isArray(candidate.allowedSelections)) return false;
        const choices = candidate.allowedSelections.filter((choice) => choice.productId === selected.productId
          && choice.variantId === selected.variantId && (!Object.prototype.hasOwnProperty.call(selected, 'options')
            || Array.isArray(selected.options) && identity(choice.options) === identity(selected.options)));
        if (choices.length !== 1) return false;
        const intent = idea.artworkIntent;
        if (!intent || intent.suggestedStyle !== brief.style) return false;
        if (intent.subject === brief.subject && intent.compositionIntent === brief.compositionIntent) return true;
        const corrected = design.letteringResolution;
        return intent.letteringMode === 'integrated' && corrected?.choice === 'edited-illustration' && corrected.confirmed === true
          && corrected.originalSubject === intent.subject && corrected.originalComposition === intent.compositionIntent
          && corrected.subject === brief.subject && corrected.compositionIntent === brief.compositionIntent;
      });
      if (matches.length !== 1) return '';
      const suggestion = matches[0].artworkIntent.wordingSuggestion;
      if (typeof suggestion !== 'string' || !suggestion.trim() || graphemes(suggestion) > MAX_WORDING_GRAPHEMES) return '';
      textModel.validateLayers([{ id: 'suggestion', text: suggestion, fontId: DEFAULT_OVERLAY_FONT_ID, fontRevision: 'r1',
        x: 0.5, y: 0.5, size: 0.05, rotation: 0, curve: 0, colour: '#26334A', align: 'center' }], { forExport: true });
      return suggestion;
    } catch (_) { return ''; }
  }

  function wordingValues(values) {
    const cleaned = (Array.isArray(values) ? values : []).map(trimOrNull).filter(Boolean);
    if (cleaned.length > MAX_WORDING || cleaned.some((value) => graphemes(value) > MAX_WORDING_GRAPHEMES)) throw new Error('INVALID_WORDING');
    return cleaned;
  }

  function resolveLettering(idea, resolution) {
    const intent = idea?.artworkIntent;
    if (!intent || !trimOrNull(intent.subject) || !trimOrNull(intent.suggestedStyle) || !trimOrNull(intent.compositionIntent)
      || !['none', 'integrated'].includes(intent.letteringMode)) throw new Error('INVALID_HANDOFF');
    if (intent.letteringMode === 'none') return { idea, letteringResolution: null };
    const subject = trimOrNull(resolution?.subject);
    const compositionIntent = trimOrNull(resolution?.compositionIntent);
    if (!subject || !compositionIntent || resolution?.confirmed !== true) throw new Error('LETTERING_CHOICE_REQUIRED');
    if (subject === intent.subject && compositionIntent === intent.compositionIntent) throw new Error('LETTERING_DESCRIPTION_REQUIRED');
    return {
      idea: { ...idea, artworkIntent: { ...intent, subject, compositionIntent, letteringMode: 'none' } },
      letteringResolution: { choice: 'edited-illustration', originalSubject: intent.subject, originalComposition: intent.compositionIntent,
        subject, compositionIntent, confirmed: true }
    };
  }

  function createWordingLayers({ wording, fontCatalogue, validateLayers }) {
    const validWording = wordingValues(wording);
    const catalogue = approvedFonts(fontCatalogue);
    const fontId = catalogue && DEFAULT_OVERLAY_FONT_ID;
    if (validWording.length && (!fontId || !trimOrNull(catalogue[fontId]?.revision))) throw new Error('UNAVAILABLE_FONT');
    const positions = [{ x: 0.5, y: 0.23, size: 0.077 }, { x: 0.5, y: 0.78, size: 0.05 }, { x: 0.5, y: 0.88, size: 0.04 }];
    const textLayers = validWording.map((text, index) => ({ id: `seed-${index + 1}`, text, fontId, fontRevision: catalogue[fontId].revision,
      ...positions[index], curve: 0, rotation: 0, colour: '#26334A', align: 'center' }));
    const verifiedLayers = typeof validateLayers === 'function' ? validateLayers(textLayers, { forExport: true }) : textLayers;
    return JSON.parse(JSON.stringify(verifiedLayers));
  }

  function createHandoff({ runtime, context, draftRevision = 0, origin, nowMs = Date.now(), selection, idea, wording, letteringResolution = null, action, fontCatalogue, validateLayers }) {
    const validWording = wordingValues(wording);
    if (!Number.isSafeInteger(draftRevision) || draftRevision < 0 || !Number.isSafeInteger(nowMs) || !selection || !selection.productId || !selection.variantId
      || !Array.isArray(selection.options) || selection.options.length > 8 || !idea || !trimOrNull(idea.designSummary) || !idea.artworkIntent
      || !runtime || !runtime.environment || !runtime.instanceId || !runtime.themeId || !runtime.workerOrigin || !context || context.schemaVersion !== 'storefront-context@1'
      || !origin || !/^https:\/\/[^/?#]+$/.test(origin)) throw new Error('INVALID_HANDOFF');
    const intent = idea.artworkIntent;
    if (!trimOrNull(intent.subject) || !trimOrNull(intent.suggestedStyle) || !trimOrNull(intent.compositionIntent) || intent.letteringMode !== 'none') throw new Error('INVALID_HANDOFF');
    if (letteringResolution !== null && (!letteringResolution || letteringResolution.choice !== 'edited-illustration' || letteringResolution.confirmed !== true
      || !trimOrNull(letteringResolution.originalSubject) || !trimOrNull(letteringResolution.originalComposition)
      || letteringResolution.subject !== intent.subject || letteringResolution.compositionIntent !== intent.compositionIntent
      || letteringResolution.originalSubject === letteringResolution.subject && letteringResolution.originalComposition === letteringResolution.compositionIntent)) throw new Error('INVALID_HANDOFF');
    const verifiedLayers = createWordingLayers({ wording: validWording, fontCatalogue, validateLayers });
    const creativeBrief = JSON.stringify({ subject: intent.subject, style: intent.suggestedStyle, compositionIntent: intent.compositionIntent,
      letteringInstruction: ILLUSTRATION_INSTRUCTION });
    if (graphemes(creativeBrief) > 4000 || graphemes(idea.designSummary) > 600) throw new Error('INVALID_HANDOFF');
    if (action !== undefined && (!action || Object.keys(action).length !== 2 || typeof action.id !== 'string'
      || !/^[A-Za-z0-9_-]{8,120}$/.test(action.id) || !['generate', 'upload'].includes(action.kind))) throw new Error('INVALID_ACTION');
    return Object.freeze({
      schemaVersion: 'dream-handoff@2', draftRevision, origin, createdAt: nowMs, expiresAt: nowMs + 7_200_000,
      context: JSON.parse(JSON.stringify(context)), selection: { productId: String(selection.productId), variantId: String(selection.variantId), options: JSON.parse(JSON.stringify(selection.options)) },
      creativeBrief, runtime: { environment: runtime.environment, instanceId: runtime.instanceId, themeId: runtime.themeId, workerOrigin: runtime.workerOrigin },
      textSchemaVersion: 1, textRendererVersion: 1, textLayers: JSON.parse(JSON.stringify(verifiedLayers)),
      ...(letteringResolution ? { letteringResolution: JSON.parse(JSON.stringify(letteringResolution)) } : {}),
      ...(action ? { action: { ...action } } : {}),
    });
  }

  const requestId = () => `discovery-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const TIMING_STAGES = new Set(['discovery-restore-start', 'discovery-restore-end', 'discovery-preparation-start',
    'discovery-human-check-wait', 'discovery-human-check-ready', 'discovery-catalogue-start', 'discovery-catalogue-end',
    'discovery-request-context', 'discovery-issue-start', 'discovery-issue-end', 'discovery-dispatch-start',
    'discovery-response', 'discovery-parsed', 'discovery-hydrated', 'discovery-rendered', 'discovery-visible']);
  // These observations carry no request authority and never persist customer input.
  function createTimingObserver({ runtime, now = () => Date.now(), log = value => root.console?.debug?.('[DIM timing]', value) } = {}) {
    return (stage, startedAt, details = {}) => {
      try {
        if (runtime?.environment !== 'development' || runtime.themeId !== '205891830103' || !TIMING_STAGES.has(stage)) return;
        const at = now();
        if (!Number.isSafeInteger(at) || at < 0 || !Number.isSafeInteger(startedAt) || startedAt < 0 || at < startedAt || at - startedAt > COMPLETED_RESULT_TTL_MS) return;
        const record = { stage, at, elapsedMs: at - startedAt };
        if (typeof details.requestId === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(details.requestId)) record.requestId = details.requestId;
        for (const key of ['draftRevision', 'entryCreatedAt', 'submissionRevision', 'submittedAt']) {
          if (Number.isSafeInteger(details[key]) && details[key] >= 0) record[key] = details[key];
        }
        if (Number.isSafeInteger(details.journeyStartedAt) && details.journeyStartedAt >= 0
          && at >= details.journeyStartedAt && at - details.journeyStartedAt <= COMPLETED_RESULT_TTL_MS) {
          record.journeyStartedAt = details.journeyStartedAt; record.journeyElapsedMs = at - details.journeyStartedAt;
        }
        if (typeof details.restored === 'boolean') record.restored = details.restored;
        if (typeof details.serverTiming === 'string' && details.serverTiming.length <= 1024) {
          const phases = {};
          for (const item of details.serverTiming.split(',')) {
            const match = /^\s*(dim_worker|dim_pre_catalogue|dim_input_moderation|dim_provider|dim_parse|dim_output_moderation|dim_final_catalogue);dur=([0-9]+(?:\.[0-9]+)?)\s*$/.exec(item);
            if (match && Number.isFinite(Number(match[2])) && Number(match[2]) <= COMPLETED_RESULT_TTL_MS) phases[match[1]] = Number(match[2]);
          }
          if (Object.keys(phases).length) record.serverTimingMs = phases;
        }
        log(JSON.stringify(record));
      } catch (_) { /* Diagnostics must never affect readiness, ownership or rendering. */ }
    };
  }
  const DISCOVERY_COPY = Object.freeze({
    heading_one: 'A suggestion for you', heading_two: 'Two suggestions for you', heading_many: 'Three suggestions for you',
    introduction_one: 'Here is a suggestion based on your idea.',
    introduction_two: 'Here are two suggestions based on your idea.',
    introduction_many: 'Here are three suggestions based on your idea.',
    ai_description_one: 'It includes a product and a description of an image we could create with AI.',
    ai_description_many: 'Each includes a product and a description of an image we could create with AI.',
    ai_instructions: 'Choose any product options, then select [generate] to create the image. You can add text or adjust the design, then preview it on your product. Or choose [upload] to use a photo or design you already have.',
    design_description_one: 'It includes a product and a design suggestion.',
    design_description_many: 'Each includes a product and a design suggestion.',
    design_instructions: 'Choose any product options, then use the action on a suggestion to make it yours. You can edit the design and preview it on your product before you order.',
    photo_note: 'Example product photo. Your chosen image will be used for your personalised preview.',
    photo_unavailable: 'Product photo unavailable.', generate: 'Generate & personalise', upload: 'Use my own image',
    action_name: '[action]: Suggestion [number], [product]',
    vague_hint: 'Tell us a hobby or interest for more personal suggestions.'
  });
  const vagueBrief = value => /^(?:idk|i don[’']?t know|not sure|no idea|anything|something|whatever|dunno)[.!?\s]*$/i.test(String(value || '').trim());
  const shortConcept = (idea, product) => {
    const title = trimOrNull(idea?.title) || trimOrNull(idea?.artworkIntent?.subject) || 'Your personal design';
    const concept = title.toLocaleLowerCase('en-GB').startsWith(`${product.title.toLocaleLowerCase('en-GB')} — `)
      ? title.slice(product.title.length + 3).trim() : title;
    return graphemes(concept) <= 64 ? concept : `${Array.from(concept).slice(0, 61).join('').replace(/\s+\S*$/, '')}…`;
  };
  const resultPresentation = (count, copy = DISCOVERY_COPY, { aiOnly = false } = {}) => {
    if (![1, 2, 3].includes(count)) return null;
    return Object.freeze({
      heading: copy[count === 1 ? 'heading_one' : count === 2 ? 'heading_two' : 'heading_many'].replace('[count]', String(count)),
      introduction: copy[count === 1 ? 'introduction_one' : count === 2 ? 'introduction_two' : 'introduction_many'].replace('[count]', String(count)),
      description: copy[`${aiOnly ? 'ai' : 'design'}_description_${count === 1 ? 'one' : 'many'}`],
      instructions: copy[aiOnly ? 'ai_instructions' : 'design_instructions'].replace('[generate]', copy.generate).replace('[upload]', copy.upload),
      productPhrase: count === 1 ? 'the suggested product' : 'any of the suggested products',
    });
  };
  const messageFor = (error) => ({
    INVALID_BUDGET: 'Enter a whole number, such as 25.', INTERESTS_REQUIRED: 'Tell us one thing they enjoy.',
    RECIPIENT_REQUIRED: 'Choose who this is for, or choose something for me.', INVALID_TEXT: 'Describe your idea in a little more detail.',
  })[error && error.message] || 'We could not prepare that request. Your answers are still here.';
  const safeJson = (value) => { try { return JSON.parse(value); } catch (_) { return null; } };
  const exactCandidates = (value) => Array.isArray(value) ? value.filter((candidate) => candidate && candidate.candidateRef && candidate.productId
    && (!Array.isArray(candidate.variants) || candidate.variants.every(variant => !Object.hasOwn(variant, 'wordsOnly') || validWordsCapability(variant.wordsOnly)))
    && (candidate.variantId && Number.isInteger(candidate.priceMinor) || Array.isArray(candidate.variants) && Array.isArray(candidate.allowedSelections))) : [];
  function catalogueProjection(payload, { runtime, context, journeyAuthority, nowMs = Date.now() }) {
    const metadata = ['journeyAuthority', 'retainedSelections', 'catalogueViews'];
    const hasMetadata = metadata.some(key => Object.hasOwn(payload || {}, key));
    if (!hasMetadata) return null;
    if (!reuseCapability(runtime) || !exactObjectKeys(payload, ['context', 'candidates', ...metadata])
      || !sameContext(payload.context, context) || !exactObjectKeys(payload.journeyAuthority, ['schemaVersion', 'reference', 'expiresAt'])
      || payload.journeyAuthority.schemaVersion !== 'journey-authority@1' || !trimOrNull(payload.journeyAuthority.reference)
      || !Number.isSafeInteger(payload.journeyAuthority.expiresAt) || payload.journeyAuthority.expiresAt <= nowMs
      || payload.journeyAuthority.reference !== journeyAuthority?.reference || payload.journeyAuthority.expiresAt !== journeyAuthority?.expiresAt
      || !Array.isArray(payload.retainedSelections) || !exactObjectKeys(payload.catalogueViews, ['createCandidates', 'uploadCandidates'])) throw new Error('CATALOGUE_UNAVAILABLE');
    for (const list of [payload.candidates, payload.catalogueViews.createCandidates, payload.catalogueViews.uploadCandidates]) {
      if (!Array.isArray(list) || exactCandidates(list).length !== list.length) throw new Error('CATALOGUE_UNAVAILABLE');
    }
    return JSON.parse(JSON.stringify({ context: payload.context, journeyAuthority: payload.journeyAuthority, views: payload.catalogueViews }));
  }
  const formatGbp = (minor) => Number.isInteger(minor) ? `£${(minor / 100).toFixed(2)}` : null;
  const candidatePrice = (candidate, selection) => {
    const variants = Array.isArray(candidate?.variants) ? candidate.variants : [];
    const selected = selection && variants.find((variant) => String(variant.variantId) === String(selection.variantId));
    if (selected && selected.currency === 'GBP') return formatGbp(selected.priceMinor);
    const prices = variants.filter((variant) => variant.currency === 'GBP' && Number.isInteger(variant.priceMinor)).map((variant) => variant.priceMinor);
    if (!prices.length && candidate?.currency === 'GBP' && Number.isInteger(candidate.priceMinor)) prices.push(candidate.priceMinor);
    if (!prices.length) return null;
    const low = Math.min(...prices); const high = Math.max(...prices);
    return low === high ? formatGbp(low) : `${formatGbp(low)}–${formatGbp(high)}`;
  };

  async function readProductPhoto(candidate, selection, { fetcher, origin, signal }) {
    try {
      if (!candidate || !/^[1-9][0-9]{0,29}$/.test(String(candidate.productId))
        || !/^\/products\/[a-z0-9][a-z0-9-]*$/.test(candidate.productPath)
        || typeof fetcher !== 'function' || selection && String(selection.productId) !== String(candidate.productId)) return '';
      const endpoint = new URL(`${candidate.productPath}.js`, origin);
      if (endpoint.origin !== origin) return '';
      const response = await fetcher(endpoint.href, { credentials: 'same-origin', redirect: 'error', ...(signal ? { signal } : {}) });
      if (!response?.ok) return '';
      const product = await response.json();
      if (String(product?.id) !== String(candidate.productId) || product.handle !== candidate.productPath.slice('/products/'.length)) return '';
      const variant = selection && Array.isArray(product.variants)
        ? product.variants.find((item) => String(item?.id) === String(selection.variantId)) : null;
      if (selection && !variant) return '';
      const image = variant?.featured_image?.src || product.featured_image;
      if (typeof image !== 'string' || !Array.isArray(product.images) || !product.images.includes(image)) return '';
      const url = new URL(image, origin);
      if (url.protocol !== 'https:' || !(url.origin === origin || url.hostname === 'cdn.shopify.com')
        || url.username || url.password) return '';
      return url.href;
    } catch (_) { return ''; }
  }

  function initialise(section) {
    section.__dimDiscoveryDispose?.();
    const config = safeJson(section.dataset.discoveryConfig) || {};
    const copy = Object.fromEntries(Object.entries(DISCOVERY_COPY).map(([key, fallback]) => [key, typeof config.copy?.[key] === 'string' && config.copy[key] ? config.copy[key] : fallback]));
    const runtimeGlobal = root && root.DIMRuntime;
    const observeTiming = createTimingObserver({ runtime: runtimeGlobal?.runtime });
    const restoreStartedAt = Date.now();
    let timingContext = {};
    let humanCheckStartedAt = null;
    const markTiming = (stage, startedAt, details = {}) => observeTiming(stage, startedAt, { ...timingContext, ...details });
    markTiming('discovery-restore-start', restoreStartedAt);
    const status = section.querySelector('[data-discovery-status]');
    const form = section.querySelector('[data-discovery-form]');
    const modeField = form.querySelector?.('input[name="mode"]') || form.elements.mode;
    const ordinary = section.dataset.discoveryOrdinary === 'true';
    let entryFacts = Object.fromEntries(ENTRY_KEYS.map(key => [key, '']));
    entryFacts.confirmedClarifications = '';
    entryFacts.sourceIntent = 'unknown'; entryFacts.photoPresent = 'false'; entryFacts.clarificationAttempts = '0';
    const clarificationField = section.querySelector('[data-discovery-clarification]');
    const clarificationQuestion = section.querySelector('[data-discovery-clarification-question]');
    const clarificationAnswer = form.elements.clarification_answer;
    const clarificationChoice = form.elements.clarification_choice;
    let correctionOptionsKey = '';
    const modeValue = () => modeField.value === 'free_text' ? 'free_text' : 'guided';
    const submit = section.querySelector('[data-discovery-submit]');
    const check = section.querySelector('[data-discovery-check]');
    const result = section.querySelector('[data-discovery-result]');
    const journey = section.querySelector('[data-journey-progress]');
    const pendingView = section.querySelector('[data-discovery-pending]');
    const pendingHeading = section.querySelector('[data-discovery-pending-heading]');
    const pendingDescription = section.querySelector('[data-discovery-pending-description]');
    const workingBar = section.querySelector('[data-discovery-working-bar]');
    const briefHeading = section.querySelector('[data-discovery-brief-heading]');
    const cancelEdit = section.querySelector('[data-discovery-cancel-edit]');
    const turnstile = section.querySelector('[data-discovery-turnstile]');
    const guided = section.querySelector('[data-discovery-guided]');
    const freeText = section.querySelector('[data-discovery-free-text]');
    const detailsField = section.querySelector('[data-discovery-details]');
    const optionalFields = section.querySelector('[data-discovery-optionals]');
    const submissionArea = section.querySelector('[data-discovery-submission]');
    const submissionPolicies = section.querySelector('[data-discovery-policies]');
    const submissionActions = section.querySelector('[data-discovery-actions]');
    const occasionDisclosure = section.querySelector('[data-discovery-occasion]');
    const budgetDisclosure = section.querySelector('[data-discovery-budget]');
    const cancelEditEarly = section.querySelector('[data-discovery-cancel-edit-early]');
    const validation = section.querySelector('[data-discovery-validation]');
    const announcer = section.querySelector('[data-discovery-announcer]');
    const coordinator = createCoordinator();
    let runtime;
    let context;
    let recordScope;
    let candidates = [];
    let candidateRoute = 'create';
    let operation = null;
    let locallyUnsent = false;
    let latest = null;
    let latestAuthority = null;
    let latestAuthorityGeneration = null;
    let latestUnderstood = null;
    let latestExpiresAt = null;
    let latestAnswers = null;
    let selectedWording = [];
    let latestWording = [];
    let editingBrief = false;
    let briefExpiresAt = null;
    let expiredView = false;
    let unavailableView = false;
    let requestIntake = null;
    let restoreEpoch = 0;
    let initialRestore = null;
    let restoringEntry = ordinary;
    let dispatchInFlight = false;
    let issueInFlight = false;
    let preparedCatalogue = null;
    let retainedCatalogue = null;
    let authorityRefresh = null;
    let authorityRefreshUsed = false;
    let expiryTimer = null;
    let photoController = null;
    let disposed = false;
    let turnstileMounted = false;
    let challenge = null;
    let challengeRevision = 0;
    let challengeBrief = null;
    let deferChallenge = false;
    let manualSubmission = null;
    let initiatedFocus = false;
    let legacyDescription = '';
    let focusCorrectionField = null;
    let invalidField = null;
    let submittedGuided = null;
    let submissionToCancel = null;
    let awaitingHumanCheck = false;
    let departing = false;
    let resumeSubmitted = () => {};

    // A widget belongs to one unsent brief revision. Completion, editing and
    // departure retire its callbacks; retained older ideas do not own a new edit.
    const retireChallenge = () => {
      challengeRevision += 1;
      challenge?.dispose(); challenge = null; challengeBrief = null; turnstileMounted = false;
      const widget = turnstile?.querySelector?.('.cf-turnstile');
      if (widget?.dataset) delete widget.dataset.discoveryTurnstileMounted;
    };

    const setStatus = (text, state) => {
      status.textContent = text && usesOriginalPhoto(requestIntake) && ['clarification', 'error'].includes(state) ? `${text} ${PHOTO_EXPLANATION}` : text;
      status.dataset.state = state || 'idle';
      projectView();
    };
    const toggle = () => {
      const purpose = form.elements.purpose.value;
      const recipient = section.querySelector('[data-discovery-recipient]');
      const visible = intakeVisibility({ purpose, recipientCustom: form.elements.recipient_custom.value, interests: form.elements.interests.value });
      if (ordinary && modeValue() === 'free_text') {
        guided.hidden = true; freeText.hidden = false;
        if (submissionArea) submissionArea.hidden = false;
        const issue = entryFacts.clarificationIssue;
        if (clarificationField) clarificationField.hidden = !issue || issue === 'budget' || Number(entryFacts.clarificationAttempts) >= 2;
        const choices = issue === 'source' ? [['upload', 'Use my photo or design'], ['create', 'Generate an Image with AI'],
          ...(['upload', 'words'].includes(candidateRoute) && runtimeGlobal.capabilities?.includes('words-only@1') && wordsCandidates(candidates).length ? [['words', 'Use words only']] : [])]
          : issue === 'purpose' ? [['self', 'For me'], ['gift', 'For somebody else']]
            : issue === 'product' ? candidates.flatMap(candidate => candidate.allowedSelections.map(selection => [
              `${selection.productId}:${selection.variantId}`, `${candidate.title}${selection.options.length ? ` — ${selection.options.map(option => option.value).join(', ')}` : ''}`])) : [];
        if (clarificationChoice) {
          clarificationChoice.hidden = !choices.length;
          const key = JSON.stringify([issue, choices]);
          if (key !== correctionOptionsKey) {
            clarificationChoice.innerHTML = `<option value="">Choose an option</option>${choices.map(([value, label]) => `<option value="${escapeAttr(value)}">${escapeHtml(label)}</option>`).join('')}`;
            clarificationChoice.value = ''; correctionOptionsKey = key;
          }
        }
        if (clarificationAnswer) clarificationAnswer.hidden = choices.length > 0;
        if (clarificationQuestion) { clarificationQuestion.textContent = entryFacts.clarificationQuestion;
          clarificationQuestion.setAttribute?.('for', choices.length ? clarificationChoice?.id || '' : clarificationAnswer?.id || ''); }
        if (budgetDisclosure) { budgetDisclosure.hidden = issue !== 'budget' && !trimOrNull(form.elements.budget.value);
          if (issue === 'budget') budgetDisclosure.open = true; }
        section.__dimDiscoveryMountTurnstile?.();
        return;
      }
      if (budgetDisclosure) budgetDisclosure.hidden = !visible.submission;
      recipient.hidden = !visible.recipient;
      if (detailsField) detailsField.hidden = !visible.details;
      if (optionalFields) optionalFields.hidden = !visible.submission;
      if (submissionArea) submissionArea.hidden = !visible.submission;
      guided.hidden = false;
      freeText.hidden = !legacyDescription;
      const helper = section.querySelector('[data-discovery-interests-help]');
      if (helper) helper.textContent = purpose === 'self'
        ? "e.g. Your hobbies, interests, anything that'll help us suggest something you'll adore"
        : "e.g. Their hobbies, interests, anything that'll help us suggest something they'll adore";
      const hint = section.querySelector('[data-discovery-vague-hint]');
      if (hint) { hint.hidden = !visible.details || !vagueBrief(form.elements.interests.value); hint.textContent = copy.vague_hint; }
      const occasionSummary = section.querySelector('[data-discovery-occasion-summary]');
      if (occasionSummary) occasionSummary.textContent = trimOrNull(form.elements.occasion.value) ? ` · ${form.elements.occasion.value.trim()}` : '';
      const budgetSummary = section.querySelector('[data-discovery-budget-summary]');
      if (budgetSummary) {
        const raw = trimOrNull(form.elements.budget.value);
        let valid = false; try { wholePoundEntry(raw); valid = true; } catch (_) {}
        budgetSummary.textContent = raw ? valid ? ` · £${raw}` : ' · Check your budget' : '';
      }
      if (visible.submission) section.__dimDiscoveryMountTurnstile?.();
    };
    const currentIntake = () => {
      wholePoundEntry(form.elements.budget.value);
      const answers = captureAnswers();
      return parseIntake({ ...answers, recipient: recipientInput(answers) });
    };
    const captureAnswers = () => ({ mode: modeValue(), purpose: String(form.elements.purpose.value || ''),
      recipient: String(form.elements.recipient.value || ''), recipientCustom: String(form.elements.recipient_custom.value || ''),
      occasion: String(form.elements.occasion.value || ''), interests: String(form.elements.interests.value || ''),
      budget: String(form.elements.budget.value || ''), freeText: String(form.elements.free_text.value || ''),
      ...(ordinary && modeValue() === 'free_text' ? { ...entryFacts, clarificationAnswer: String(clarificationAnswer?.value || '') } : {}) });
    const captureWording = () => [...selectedWording];
    const briefState = () => JSON.stringify({ answers: captureAnswers(), wording: captureWording() });
    let retainedBriefState = briefState();
    const independentWording = () => entryFacts.wordingOwned === 'true' || captureWording().length ? captureWording()
      : typeof latestUnderstood?.wording === 'string' && latestUnderstood.wording.trim() ? [latestUnderstood.wording] : null;
    const recommendationAnswersKey = (answers) => {
      try {
        const intake = parseIntake({ ...answers, recipient: recipientInput(answers) });
        if (intake.entryMode === 'free_text') {
          const { sourceIntent = 'unknown', productConstraint = null, selectedProduct = null, wording = null, photoPresent = false, ...legacy } = intake;
          return JSON.stringify({ ...legacy, sourceIntent, productConstraint, selectedProduct, wording, photoPresent });
        }
        return JSON.stringify(intake);
      } catch (_) { return null; }
    };
    const answersMatchIdeas = () => latestAnswers !== null && recommendationAnswersKey(captureAnswers()) !== null
      && recommendationAnswersKey(captureAnswers()) === recommendationAnswersKey(latestAnswers);
    const refreshIdeaEligibility = ({ announce = false } = {}) => {
      const stale = Boolean(latest && (editingBrief || !answersMatchIdeas()));
      for (const button of result.querySelectorAll?.('[data-discovery-use]') || []) button.disabled = stale || dispatchInFlight || Boolean(operation);
      if (announce && stale && !operation && !editingBrief) setStatus('Those suggestions use your earlier answers. Select Find new suggestions to refresh them.', 'stale');
      return stale;
    };
    const restoreAnswers = (answers, wording) => {
      retireChallenge();
      modeField.value = answers.mode; form.elements.purpose.value = answers.purpose;
      if (ordinary && answers.mode === 'free_text' && !Object.hasOwn(answers, 'sourceIntent')) form.elements.purpose.value = '';
      form.elements.recipient.value = answers.recipient; form.elements.recipient_custom.value = answers.recipient === 'someone-else'
        ? answers.recipientCustom : RECIPIENT_PRESETS[answers.recipient] || '';
      form.elements.occasion.value = answers.occasion; form.elements.interests.value = answers.interests;
      form.elements.budget.value = answers.budget; form.elements.free_text.value = answers.freeText;
      selectedWording = validWording(wording) || [];
      if (ordinary && Object.hasOwn(answers, 'sourceIntent')) {
        entryFacts = Object.fromEntries([...ENTRY_KEYS, 'confirmedClarifications'].map(key => [key, String(answers[key] || '')]));
        if (clarificationAnswer) clarificationAnswer.value = answers.clarificationAnswer;
      }
      const legacyDescriptionNode = section.querySelector('[data-discovery-legacy-description]');
      if (answers.mode === 'free_text') legacyDescription = answers.freeText;
      if (legacyDescriptionNode) legacyDescriptionNode.textContent = legacyDescription;
      toggle();
      retainedBriefState = briefState();
    };
    const recipientText = (answers) => {
      if (answers.purpose === 'self') return 'Something for me';
      if (answers.recipient === 'someone-else') return trimOrNull(answers.recipientCustom) || 'Not added yet';
      return RECIPIENT_PRESETS[answers.recipient] || 'Not added yet';
    };
    const budgetText = (value) => {
      const raw = trimOrNull(value);
      if (!raw) return 'No budget set';
      try { positiveBudget(raw); return `Up to £${raw} for the product`; }
      catch (_) { return `Needs checking: ${raw}`; }
    };
    const answerRows = (answers = captureAnswers(), understood = null) => {
      const rows = answers.mode === 'free_text'
        ? [['Your idea', trimOrNull(answers.freeText) || 'Not added yet']]
        : [['For', recipientText(answers)], ['Enjoys', trimOrNull(answers.interests) || 'Not added yet'],
          ['Occasion', trimOrNull(answers.occasion) || 'Not specified']];
      let displayBudget = answers.budget;
      const canonicalBudget = understood?.requestedBudgetGbp;
      if (canonicalBudget === null) displayBudget = '';
      else if (typeof canonicalBudget === 'number') {
        try { positiveBudget(String(canonicalBudget)); displayBudget = String(canonicalBudget); }
        catch (_) { /* Malformed summaries cannot replace the retained input. */ }
      }
      rows.push(['Budget', budgetText(displayBudget)]);
      return rows;
    };
    const clearValidation = () => {
      if (invalidField?.removeAttribute) {
        const field = invalidField;
        field.removeAttribute('aria-invalid');
        const description = String(field.getAttribute?.('aria-describedby') || '').split(/\s+/).filter(id => id && id !== validation?.id);
        if (description.length) field.setAttribute('aria-describedby', description.join(' '));
        else field.removeAttribute('aria-describedby');
      }
      invalidField = null;
      if (!validation) return;
      validation.hidden = true; validation.textContent = '';
    };
    const projectView = ({ focus = false } = {}) => {
      const preparingSubmission = Boolean(submittedGuided && !editingBrief || awaitingHumanCheck && manualSubmission) && !entryFacts.clarificationIssue
        && !['blocked', 'error', 'uncertain'].includes(status.dataset.state);
      const pending = restoringEntry || preparingSubmission || dispatchInFlight || Boolean(operation) || coordinator.state().status !== 'idle';
      if (!ordinary && !pending && !latest && modeValue() === 'free_text') {
        modeField.value = 'guided'; form.elements.purpose.value = '';
        form.elements.recipient.value = 'someone-else'; form.elements.recipient_custom.value = '';
        form.elements.interests.value = '';
      }
      toggle();
      const uncertain = pending && (status.dataset.state === 'uncertain' || coordinator.state().status === 'uncertain');
      const ideas = !pending && !editingBrief && latest && latestExpiresAt > Date.now() && answersMatchIdeas();
      const view = pending ? 'pending' : ideas ? 'ideas' : expiredView ? 'expired' : unavailableView ? 'unavailable' : 'brief';
      section.dataset.discoveryView = view;
      if (journey) {
        const step = ideas ? 2 : 1;
        if (root.DreamItJourneyRoutes?.projectProgress) {
          root.DreamItJourneyRoutes.projectProgress(journey, { family: 'guided', currentStep: step, completed: ideas ? [1] : [] });
        } else {
          journey.dataset.journeyStep = String(step);
        }
      }
      // The widget stays in its existing form. Only the check is exposed while
      // an already-submitted idea waits; its editable fields remain retained.
      form.hidden = pending && !awaitingHumanCheck || Boolean(ideas);
      section.dataset.discoveryAwaitingCheck = String(pending && awaitingHumanCheck);
      if (pending) {
        if (guided) guided.hidden = true;
        if (freeText) freeText.hidden = true;
        if (budgetDisclosure) budgetDisclosure.hidden = true;
        if (validation) validation.hidden = true;
      }
      if (submissionPolicies) submissionPolicies.hidden = pending;
      if (submissionActions) submissionActions.hidden = pending;
      submit.hidden = pending;
      status.hidden = pending && !uncertain && ['idle', 'loading'].includes(status.dataset.state || 'idle');
      if (pendingView) pendingView.hidden = !pending;
      if (pendingHeading) pendingHeading.textContent = uncertain ? 'Check your earlier request' : 'Preparing your suggestions…';
      if (pendingDescription) pendingDescription.textContent = uncertain
        ? 'Its outcome is not confirmed. Check the status before starting another request.'
        : restoringEntry ? 'We are checking your retained idea and any earlier request before continuing.'
          : awaitingHumanCheck ? 'Your submitted idea is retained. Complete Human Check when it appears to continue.'
            : 'We are checking suitable products and preparing up to three suggestions. Your image is created only when you choose to create it.';
      if (pendingDescription) pendingDescription.hidden = !pending;
      if (workingBar) workingBar.hidden = !pending || uncertain;
      result.hidden = !ideas;
      if (turnstile) turnstile.hidden = deferChallenge || pending && !awaitingHumanCheck || Boolean(ideas) || Boolean(validateDiscoveryStep(null, captureAnswers()))
        || Boolean(challenge?.peek()) || Boolean(entryFacts.clarificationIssue && !trimOrNull(clarificationAnswer?.value) && !clarificationChoice?.value);
      if (cancelEdit) cancelEdit.hidden = !editingBrief || !latest;
      if (cancelEditEarly) cancelEditEarly.hidden = !editingBrief || !latest || !submissionArea?.hidden;
      submit.textContent = ordinary && entryFacts.clarificationIssue ? 'Continue' : latest && editingBrief ? 'Find new suggestions' : 'Find suggestions';
      if (ordinary && Number(entryFacts.clarificationAttempts) >= 2) submit.disabled = true;
      if (focus) (pending ? pendingHeading : ideas ? result.querySelector?.('[data-discovery-results-heading]') : briefHeading)?.focus?.();
      if (initiatedFocus && !pending) {
        if (root.document?.activeElement === pendingHeading)
          (ideas ? result.querySelector?.('[data-discovery-results-heading]') : briefHeading)?.focus?.();
        else if (ideas && announcer) announcer.textContent = 'Your suggestions are ready.';
        initiatedFocus = false;
      }
    };
    const showValidation = (issue) => {
      clearValidation();
      if (validation) { validation.textContent = issue.message; validation.hidden = false; validation.dataset.code = issue.code || ''; }
      if (issue.field === 'budget' && budgetDisclosure) budgetDisclosure.open = true;
      if (issue.field === 'occasion' && occasionDisclosure) occasionDisclosure.open = true;
      const field = form.elements[issue.field];
      invalidField = field;
      field?.setAttribute?.('aria-invalid', 'true');
      if (validation?.id && field?.setAttribute) {
        const descriptions = new Set(String(field.getAttribute?.('aria-describedby') || '').split(/\s+/).filter(Boolean));
        descriptions.add(validation.id); field.setAttribute('aria-describedby', [...descriptions].join(' '));
      }
      if (field?.focus) field.focus();
      else if (field?.[0]?.focus) field[0].focus();
    };
    section.__dimDiscoveryMountTurnstile = () => {
      if (disposed || departing || deferChallenge || turnstileMounted || submissionArea?.hidden || operation || latest && !editingBrief || validateDiscoveryStep(null, captureAnswers())
        || !turnstile || typeof root.turnstile?.render !== 'function') return;
      const widget = turnstile.querySelector?.('.cf-turnstile');
      if (!widget) return;
      if (widget.dataset?.discoveryTurnstileMounted === 'true') { turnstileMounted = true; return; }
      try {
        const revision = challengeRevision;
        const answers = briefState();
        challengeBrief = answers;
        const ownsUnsentTask = () => !disposed && !departing && revision === challengeRevision && answers === briefState()
          && !operation && coordinator.state().status === 'idle' && (!latest || editingBrief);
        challenge = createChallengeController(root.turnstile, widget, () => {
          void Promise.resolve().then(() => { if (ownsUnsentTask()) {
            if (humanCheckStartedAt !== null && challenge?.peek()) { markTiming('discovery-human-check-ready', humanCheckStartedAt); humanCheckStartedAt = null; }
            projectView(); if (awaitingHumanCheck && challenge?.peek()) resumeSubmitted();
          } });
        }, async (problem) => {
          if (!ownsUnsentTask() || dispatchInFlight || !submittedGuided && !manualSubmission) return;
          if (submittedGuided && !(await bindingCurrent())) return;
          if (!ownsUnsentTask() || dispatchInFlight || challenge?.peek()) return;
          if (problem === 'expired' && awaitingHumanCheck) {
            setStatus('Your submitted idea is retained while Human Check renews. Complete it when it appears to continue.', 'loading'); return;
          }
          setStatus(awaitingHumanCheck
            ? 'Human Check could not complete or expired. Complete it again; your submitted idea will continue once it is ready. Your idea has not been sent.'
            : 'Human Check could not complete or expired. Complete it again, then select Find suggestions. Your idea has not been sent.', 'blocked');
        });
        challenge.mount();
        turnstileMounted = true;
        if (widget.dataset) widget.dataset.discoveryTurnstileMounted = 'true';
      } catch (_) { /* token validation continues to block a request if rendering is unavailable */ }
    };
    const runtimeConfigured = () => {
      try {
        if (!runtimeGlobal || typeof runtimeGlobal.verify !== 'function' || typeof runtimeGlobal.authenticatedFetch !== 'function' || !root.DIMRuntimeContext) throw new Error('DIM_RUNTIME_UNAVAILABLE');
        runtime = runtimeGlobal.runtime;
        if (!runtime || !runtimeGlobal.features || runtimeGlobal.features.discovery !== true || runtimeGlobal.features.text !== true
          || !budget || budget.schemaVersion !== 'dim-budget@1' || typeof budget.reconcileBudget !== 'function'
          || !approvedFonts(runtimeGlobal.fontCatalogue) || !Object.keys(approvedFonts(runtimeGlobal.fontCatalogue)).length
          || !runtimeGlobal.scene?.keys?.pending || typeof runtimeGlobal.requireAdmission !== 'function' || !turnstile?.querySelector?.('.cf-turnstile')) throw new Error('DIM_RUNTIME_UNAVAILABLE');
        return true;
      } catch (_) {
        submit.disabled = true;
        setStatus('Suggestions are unavailable until this development experience is configured. You can still choose a product or upload an image.', 'blocked');
        return false;
      }
    };
    const ensureRuntime = async (isCurrent = () => true) => {
      if (!runtimeConfigured()) return false;
      try {
        const verified = await runtimeGlobal.verify();
        if (!isCurrent()) return false;
        context = verified;
        const scope = root.DIMRuntimeContext.runtimeScope(runtime);
        const keys = root.DIMRuntimeContext.storageKeys(runtime, context.storefrontId, context.purpose, { bindingId: context.bindingId, mode: context.mode });
        if (!scope || !keys || !keys.recordScope) throw new Error('DIM_RUNTIME_UNAVAILABLE');
        recordScope = keys.recordScope;
        return true;
      } catch (_) {
        if (isCurrent()) setStatus('Suggestions are unavailable until this development experience is configured. Your answers are still here.', 'blocked');
        return false;
      }
    };
    const loadCandidates = async (route = 'create', isCurrent = () => true, allowRefresh = true) => {
      const startedAt = Date.now();
      markTiming('discovery-catalogue-start', startedAt);
      const authority = runtimeGlobal.journeyAuthority;
      const validRetained = value => value && reuseCapability(runtimeGlobal) && runtimeGlobal.hasJourneyDataReuse === true
        && sameContext(value.context, context) && (value.journeyAuthority?.reference === authority?.reference
          || Number.isSafeInteger(value.authorityGeneration) && value.authorityGeneration === runtimeGlobal.authorityGeneration)
        && value.journeyAuthority?.expiresAt === authority?.expiresAt && authority.expiresAt > Date.now();
      if (!validRetained(retainedCatalogue) && authority) {
        try {
          const raw = root.sessionStorage.getItem(`${runtimeGlobal.scene.keys.pending}:recommendation:catalogue`);
          if (raw?.length <= 524288) {
            const saved = JSON.parse(raw);
            if (saved.runtime === runtimeIdentity(runtime) && saved.recordScope === recordScope) {
              const sameGeneration = Number.isSafeInteger(saved.authorityGeneration) && saved.authorityGeneration === runtimeGlobal.authorityGeneration;
              const projection = catalogueProjection(saved.payload, { runtime: runtimeGlobal, context,
                journeyAuthority: sameGeneration ? saved.payload.journeyAuthority : authority });
              if (projection && sameGeneration) projection.authorityGeneration = saved.authorityGeneration;
              if (validRetained(projection)) retainedCatalogue = projection;
            }
          }
        } catch (_) { /* An unavailable retained projection falls back to authenticated acquisition. */ }
      }
      if (validRetained(retainedCatalogue)) {
        if (!isCurrent()) return null;
        const loaded = retainedCatalogue.views[route === 'create' ? 'createCandidates' : 'uploadCandidates'];
        candidates = route === 'words' ? wordsCandidates(loaded) : loaded;
        candidateRoute = route;
        markTiming('discovery-catalogue-end', startedAt);
        return candidates;
      }
      let cataloguePath = config.cataloguePath;
      if (['upload', 'words'].includes(route)) { const url = new URL(cataloguePath, runtime.workerOrigin); url.searchParams.set('route', 'upload'); cataloguePath = `${url.pathname}${url.search}`; }
      const response = await runtimeGlobal.authenticatedFetch(cataloguePath, { method: 'GET' });
      const payload = await response.json();
      if (!response.ok) {
        if (allowRefresh && await refreshCurrentAuthority(payload?.error?.code || payload?.code, isCurrent)) return loadCandidates(route, isCurrent, false);
        throw new Error('CATALOGUE_UNAVAILABLE');
      }
      const projection = catalogueProjection(payload, { runtime: runtimeGlobal, context, journeyAuthority: runtimeGlobal.journeyAuthority });
      const loaded = exactCandidates(payload.candidates || payload);
      if (!loaded.length) throw new Error('CATALOGUE_UNAVAILABLE');
      if (!isCurrent()) return null;
      if (projection) {
        if (Number.isSafeInteger(runtimeGlobal.authorityGeneration)) projection.authorityGeneration = runtimeGlobal.authorityGeneration;
        retainedCatalogue = projection;
        try {
          const raw = JSON.stringify({ runtime: runtimeIdentity(runtime), recordScope, payload,
            ...(Number.isSafeInteger(runtimeGlobal.authorityGeneration) ? { authorityGeneration: runtimeGlobal.authorityGeneration } : {}) });
          if (raw.length <= 524288) root.sessionStorage.setItem(`${runtimeGlobal.scene.keys.pending}:recommendation:catalogue`, raw);
        } catch (_) { /* Navigation retention is optional and never extends authority. */ }
      }
      candidates = route === 'words' ? wordsCandidates(loaded) : loaded;
      candidateRoute = route;
      markTiming('discovery-catalogue-end', startedAt);
      return candidates;
    };
    const refreshCurrentAuthority = async (code, isCurrent = () => !disposed && !departing) => {
      if (!reuseCapability(runtimeGlobal) || !refreshableAuthority(code) || typeof runtimeGlobal.refreshAuthority !== 'function') return false;
      if (authorityRefresh) return authorityRefresh;
      if (authorityRefreshUsed || !isCurrent()) return false;
      authorityRefreshUsed = true;
      authorityRefresh = (async () => {
        try {
          const refreshed = await runtimeGlobal.refreshAuthority();
          if (!isCurrent()) return false;
          const next = runtimeGlobal.context || refreshed?.context || refreshed;
          continuation.validateContext(next);
          // A new context cannot inherit suggestions or an in-flight paid result.
          // Keep their owned records and content for recovery; only new actions use it.
          context = next;
          retainedCatalogue = null; preparedCatalogue = null;
          return true;
        } catch (_) { return false; }
        finally { authorityRefresh = null; }
      })();
      return authorityRefresh;
    };
    const recommendationKey = () => {
      const key = runtimeGlobal.scene?.keys?.pending;
      return key ? `${key}:recommendation` : null;
    };
    const completedKey = () => {
      const key = recommendationKey();
      return key ? `${key}:completed` : null;
    };
    const briefKey = () => { const key = recommendationKey(); return key ? `${key}:brief` : null; };
    const readStored = (key) => { try { return key ? root.sessionStorage.getItem(key) : null; } catch (_) { return null; } };
    const removeStored = (key) => { try { if (key) root.sessionStorage.removeItem(key); } catch (_) {
      try { if (key) root.sessionStorage.setItem(key, ''); } catch (_) { /* storage is optional after a terminal result */ }
    } };
    const turnstileToken = () => challenge?.peek() || null;
    const persistOperation = () => {
      const key = recommendationKey();
      if (!key || !operation) throw new Error('PENDING_UNAVAILABLE');
      const record = JSON.stringify({ schemaVersion: 'dim-recommendation-pending@1', recordScope, operation });
      root.sessionStorage.setItem(key, record);
      if (root.sessionStorage.getItem(key) !== record) throw new Error('PENDING_UNAVAILABLE');
    };
    const removeOperation = () => removeStored(recommendationKey());
    const removeCompleted = () => removeStored(completedKey());
    const persistBrief = ({ cancelledPreparation = false } = {}) => {
      if (disposed || operation || dispatchInFlight && !cancelledPreparation || !recordScope) return false;
      try {
        const nowMs = Date.now();
        if (!briefExpiresAt) briefExpiresAt = latestExpiresAt || nowMs + COMPLETED_RESULT_TTL_MS;
        if (briefExpiresAt <= nowMs) return false;
        const key = briefKey();
        if (!key) return false;
        const raw = JSON.stringify(createBriefRecord({ runtime, context, recordScope,
          answers: captureAnswers(), wording: captureWording(), editing: editingBrief, nowMs, expiresAt: briefExpiresAt }));
        root.sessionStorage.setItem(key, raw);
        const saved = readStored(key) === raw;
        if (saved) retainedBriefState = briefState();
        return saved;
      } catch (_) { return false; /* optional local restoration never grants request or result authority */ }
    };
    const writeSummary = () => {};
    const persistCompleted = (payload) => {
      try {
        const key = completedKey();
        if (!key) return null;
        const record = createCompletedResultRecord({ runtime, context, recordScope,
          answers: operation?.answers || captureAnswers(), wording: operation?.wording || captureWording(),
          result: payload, candidates, journeyAuthority: operation?.journeyAuthority || runtimeGlobal.journeyAuthority,
          authorityGeneration: operation?.authorityGeneration ?? runtimeGlobal.authorityGeneration, nowMs: Date.now() });
        const raw = JSON.stringify(record);
        if (raw.length > MAX_COMPLETED_RECORD_CHARS) return null;
        root.sessionStorage.setItem(key, raw);
        if (readStored(key) !== raw || !restoreCompletedResult(raw, { runtime, context, recordScope, nowMs: Date.now() })) return null;
        return record;
      } catch (_) { return null; /* retaining ideas is optional and must not change a completed request into an uncertain one */ }
    };
    const persistCurrentWording = () => {
      try {
        const key = completedKey(); const raw = readStored(key);
        const restored = restoreCompletedResult(raw, { runtime, context, recordScope, nowMs: Date.now() });
        const wording = validWording(captureWording());
        if (!restored) return !latest;
        if (!wording) return false;
        const record = JSON.parse(raw); record.wording = wording;
        const updated = JSON.stringify(record);
        if (updated.length > MAX_COMPLETED_RECORD_CHARS) return false;
        root.sessionStorage.setItem(key, updated);
        return readStored(key) === updated;
      } catch (_) { return false; /* a retained result is useful but never required for handoff */ }
    };
    const settleOperation = () => {
      retireChallenge();
      deferChallenge = true;
      manualSubmission = null; awaitingHumanCheck = false;
      const completedRequestId = operation?.requestId;
      if (completedRequestId) coordinator.finish(completedRequestId, 'completed');
      removeOperation();
      operation = null;
    };
    const scheduleIdeasExpiry = () => {
      if (expiryTimer) root.clearTimeout?.(expiryTimer);
      if (!Number.isSafeInteger(latestExpiresAt) || latestExpiresAt <= Date.now()) return;
      expiryTimer = root.setTimeout?.(() => {
        if (disposed) return;
        if (latestExpiresAt > Date.now()) return scheduleIdeasExpiry();
        latest = null; latestAnswers = null; latestWording = []; latestExpiresAt = null;
        editingBrief = false; expiredView = true; removeCompleted();
        setStatus('Those earlier suggestions have expired. Find suggestions to request new ones.', 'error');
      }, latestExpiresAt - Date.now());
      expiryTimer?.unref?.();
    };
    const hydrateResult = async (payload, { expectedPlannerRequestId = operation?.plannerRequestId, settle = true, retain = true, restored = false,
       submittedAnswers = operation?.answers || captureAnswers(),
       submittedWording = operation?.wording || captureWording(),
       expiresAt = Date.now() + COMPLETED_RESULT_TTL_MS, journeyAuthorityReference = runtimeGlobal.journeyAuthority?.reference || null,
       authorityGeneration = runtimeGlobal.authorityGeneration ?? null } = {}) => {
      if (!trimOrNull(expectedPlannerRequestId) || payload?.requestId !== expectedPlannerRequestId || !sameContext(payload.context, context)) return false;
      if (Number.isSafeInteger(operation?.authorityGeneration) ? operation.authorityGeneration !== runtimeGlobal.authorityGeneration
        : operation?.journeyAuthority && operation.journeyAuthority.reference !== runtimeGlobal.journeyAuthority?.reference) return false;
      if (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now()) return false;
      if (payload.status === 'clarification') {
        if (!settle) return false;
        writeSummary(requestIntake, payload.understood);
        settleOperation();
        unavailableView = false;
        if (ordinary) {
          const progress = clarificationProgress(entryFacts.clarificationIssue ? { issue: entryFacts.clarificationIssue, attempts: Number(entryFacts.clarificationAttempts) } : null, payload.clarificationDetail);
          entryFacts.clarificationIssue = progress.issue; entryFacts.clarificationAttempts = String(progress.attempts);
          entryFacts.clarificationBrief = normalizedBrief(form.elements.free_text.value);
          entryFacts.clarificationQuestion = progress.question || String(payload.clarification || 'Tell us a little more about your idea.');
          if (progress.issue === 'source' && candidateRoute !== 'upload' && runtimeGlobal.capabilities?.includes('words-only@1')) {
            try { await loadCandidates('upload'); } catch (_) { /* unavailable capability cannot offer Words only */ }
          }
          if (clarificationAnswer) clarificationAnswer.value = '';
          persistBrief();
          setStatus(progress.stopped ? 'We have not resolved that detail after two attempts. Your idea and answers are retained. Choose a product, use your photo or generate an image with AI below.' : entryFacts.clarificationQuestion, 'clarification');
        } else setStatus(payload.clarification || 'Please clarify your request before choosing an idea.', 'clarification');
        return true;
      }
      if (payload.status === 'no_match') {
        if (!settle) return false;
        writeSummary(requestIntake, payload.understood);
        settleOperation();
        unavailableView = false;
        setStatus(payload.understood?.sourceIntent === 'words'
          ? 'We could not find a current product option that supports Words only. Your wording is retained. You can choose a product and use a photo or deliberately generate an image with AI.'
          : 'We could not find a current product for that request. Your brief and answers are retained. Choose a product, use your photo or generate an image with AI below.', 'error');
        return true;
      }
      if (payload.status !== 'ideas') throw new Error('INVALID_RESPONSE');
      if (ordinary && !restored) {
        const route = usesOriginalPhoto(requestIntake) ? 'upload' : ['upload', 'words'].includes(payload.understood?.sourceIntent) ? payload.understood.sourceIntent : 'create';
        if (candidateRoute !== route) await loadCandidates(route);
        // Only an explicit selector in the submitted request can narrow the
        // trusted catalogue projection. Model-supplied identities never do so.
        candidates = constrainCandidates(candidates, requestIntake?.selectedProduct || null);
      }
      const ideas = Array.isArray(payload.ideas) ? payload.ideas.map(idea => projectPhotoRecommendation(idea, requestIntake)) : [];
      const hydrated = ideas.map((idea) => {
        const candidate = candidates.find((value) => value.candidateRef === idea.candidateRef);
        const product = idea.product;
        const productMatches = product && candidate && product.productId === candidate.productId && product.title === candidate.title
          && product.summary === candidate.summary && product.productPath === candidate.productPath && product.selectionRequired === candidate.selectionRequired;
        const selection = idea.selection === null ? null : selectExactCandidate(candidates, idea);
        return { idea, candidate, product, selection, productMatches };
      }).filter((entry) => entry.candidate && entry.productMatches && (entry.selection || entry.idea.selection === null)
        && entry.idea && trimOrNull(entry.idea.designSummary));
      if (!hydrated.length) throw new Error('INVALID_RESPONSE');
      const hydrationStartedAt = Date.now();
      markTiming('discovery-hydrated', timingContext.responseParsedAt || hydrationStartedAt,
        { requestId: payload.requestId, draftRevision: payload.draftRevision, restored });
      photoController?.abort?.();
      photoController = null;
      latest = hydrated; latestUnderstood = projectPhotoUnderstanding(payload.understood, requestIntake);
      latestAuthority = journeyAuthorityReference;
      latestAuthorityGeneration = authorityGeneration;
      retireChallenge();
      const retained = settle && retain ? persistCompleted(payload) : null;
      latestExpiresAt = retained?.expiresAt || expiresAt;
      if (settle && retain) { briefExpiresAt = latestExpiresAt; removeStored(briefKey()); }
      latestAnswers = { ...submittedAnswers };
      legacyDescription = latestAnswers.mode === 'free_text' ? latestAnswers.freeText : '';
      const legacyDescriptionNode = section.querySelector('[data-discovery-legacy-description]');
      if (legacyDescriptionNode) legacyDescriptionNode.textContent = legacyDescription;
      latestWording = validWording(submittedWording) || [];
      editingBrief = false; expiredView = false; unavailableView = false;
      scheduleIdeasExpiry();
      const submittedSummary = answerRows(latestAnswers, latestUnderstood)
        .map(([label, value]) => `<div class="dim-discovery__answer"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('');
      // The supported result contract owns source intent for the entire set.
      // Only explicit Create establishes AI-only; Words, photo, Upload and
      // legacy/unknown sets use the truthful general design explanation.
      const aiCards = !usesOriginalPhoto(requestIntake) && payload.understood?.sourceIntent === 'create';
      const presentation = resultPresentation(hydrated.length, copy, { aiOnly: aiCards });
      result.dataset.discoveryCount = String(hydrated.length);
      result.innerHTML = `<div class="dim-discovery__submitted-brief"><div class="dim-discovery__results-head"><div><p class="eyebrow">Suggestions</p><h2 data-discovery-results-heading tabindex="-1">${escapeHtml(presentation.heading)}</h2></div><button class="dim-discovery__link" type="button" data-discovery-edit-brief>Edit your idea</button></div><p class="dim-discovery__results-copy">${escapeHtml(presentation.introduction)} ${escapeHtml(presentation.description)}</p><p class="dim-discovery__results-copy">${escapeHtml(presentation.instructions)}</p>${usesOriginalPhoto(requestIntake) ? `<p data-discovery-photo-capability>${escapeHtml(PHOTO_EXPLANATION)}</p>` : ''}<dl>${submittedSummary}</dl></div>` + hydrated.map((entry, index) => {
        const fixed = entry.selection || (entry.candidate.allowedSelections.length === 1 ? entry.candidate.allowedSelections[0] : null);
        const choices = fixed ? '' : optionChoices(entry.candidate).map((option) => `<label class="dim-discovery__field">${escapeHtml(option.name)}${option.values.length === 1 ? `: ${escapeHtml(option.values[0])}` : ''}<select data-discovery-option="${escapeAttr(option.name)}" aria-describedby="DimIdeaStatus-${index}" aria-label="${escapeAttr(`${option.name} for Suggestion ${index + 1}: ${entry.product.title}`)}"${option.values.length === 1 ? ' hidden' : ''}>${option.values.length === 1 ? '' : `<option value="">Choose ${escapeHtml(option.name)}</option>`}${option.values.map((value) => `<option value="${escapeAttr(value)}">${escapeHtml(value)}</option>`).join('')}</select></label>`).join('');
        const lettering = entry.idea.artworkIntent?.letteringMode === 'integrated'
          ? `<fieldset class="dim-discovery__lettering" data-discovery-lettering><legend>Keep the wording editable</legend><p>This idea asks for wording inside the image. Edit the image description so the wording can be added separately in the editor.</p><label class="dim-discovery__field" for="DimDiscoverySubject-${index}">Image subject<input id="DimDiscoverySubject-${index}" data-discovery-lettering-subject type="text" value="${escapeAttr(entry.idea.artworkIntent.subject)}" maxlength="1000"></label><label class="dim-discovery__field" for="DimDiscoveryComposition-${index}">Image description<textarea id="DimDiscoveryComposition-${index}" data-discovery-lettering-composition maxlength="1000">${escapeHtml(entry.idea.artworkIntent.compositionIntent)}</textarea></label><label class="dim-discovery__choice"><input type="checkbox" data-discovery-lettering-confirm><span>Keep wording editable in the editor</span></label><p><button class="dim-discovery__link" type="button" data-discovery-return-ideas>Choose another suggestion</button></p></fieldset>`
          : '';
        const variant = fixed ? escapeAttr(fixed.variantId) : '';
        const price = candidatePrice(entry.candidate, fixed);
        const photo = usesOriginalPhoto(requestIntake);
        const wordsOnly = payload.understood?.sourceIntent === 'words';
        const actions = photo ? [['personalise', `Personalise ${entry.product.title} with your photo`]] : wordsOnly ? [['personalise', 'Personalise']]
          : [['generate', copy.generate], ['upload', copy.upload]];
        const alternative = photo ? `<details><summary>Use a different image</summary><p>Use your accepted image, or choose Replace Image in the editor to upload a different photo or design. Generating creates a new image; it does not edit your attached photo.</p><label class="dim-discovery__field" for="DimNewImage-${index}">Describe your new image<textarea id="DimNewImage-${index}" data-discovery-new-image maxlength="1000" aria-describedby="DimIdeaStatus-${index}"></textarea></label>${[['upload', 'Use my own image'], ['generate', 'Generate an Image with AI']].map(([action, label]) => `<button class="button button--secondary" type="button" data-discovery-use="${escapeAttr(entry.candidate.candidateRef)}" data-discovery-action="${action}" data-discovery-variant="${variant}">${label}</button>`).join('')}</details>` : '';
        const suggestion = trimOrNull(entry.idea.artworkIntent?.wordingSuggestion);
        const wording = suggestion && graphemes(suggestion) <= MAX_WORDING_GRAPHEMES
          ? `<label class="dim-discovery__field">Optional words<input type="text" data-discovery-wording value="${escapeAttr(independentWording() !== null ? independentWording().join('\n') : entryFacts.wordingText || suggestion)}" maxlength="120"><span class="dim-discovery__help">These stay editable in the editor.</span></label>` : '';
        // Photos stay hidden until load; lazy loading would wait for visibility and never start.
        return `<article class="dim-discovery__idea${index === 0 ? ' dim-discovery__idea--featured' : ''}" data-discovery-idea="${escapeAttr(entry.candidate.candidateRef)}"><div class="dim-discovery__media"><img data-discovery-photo hidden alt="${escapeAttr(`${entry.product.title} example product photo`)}" loading="eager"><span data-discovery-photo-fallback>${escapeHtml(copy.photo_unavailable)}</span></div><p class="dim-discovery__photo-note" data-discovery-photo-note hidden>${escapeHtml(copy.photo_note)}</p><h2>${escapeHtml(entry.product.title)} — ${escapeHtml(shortConcept(entry.idea, entry.product))}</h2><p class="dim-discovery__concept">${escapeHtml(entry.idea.designSummary)}</p>${trimOrNull(entry.idea.reason) ? `<p class="dim-discovery__reason"><strong>Why we suggested this:</strong> ${escapeHtml(entry.idea.reason)}</p>` : ''}${price ? `<p class="dim-discovery__price" data-discovery-price>${escapeHtml(price)}</p>` : ''}${fixed?.options?.length ? `<p class="dim-discovery__selected-option">${fixed.options.map(option => `${escapeHtml(option.name)}: ${escapeHtml(option.value)}`).join(' · ')}</p>` : ''}${fixed ? '' : `<fieldset class="dim-discovery__options" aria-label="${escapeAttr(`Product options for Suggestion ${index + 1}: ${entry.product.title}`)}">${choices}</fieldset>`}${wordsOnly ? '<p>Start with the suggested text, then add or edit your wording in the editor.</p>' : ''}${wording}${lettering}<p id="DimIdeaStatus-${index}" data-discovery-card-status hidden role="status" aria-live="polite"></p><div class="dim-discovery__idea-actions">${actions.map(([action, label], actionIndex) => `<button class="button${actionIndex === 0 ? '' : ' button--secondary'}" type="button" data-discovery-use="${escapeAttr(entry.candidate.candidateRef)}" data-discovery-action="${action}" aria-label="${escapeAttr(copy.action_name.replace('[action]', label).replace('[number]', String(index + 1)).replace('[product]', entry.product.title))}" data-discovery-variant="${variant}">${escapeHtml(label)}</button>`).join('')}</div>${alternative}</article>`;
      }).join('');
      if (typeof root.window?.fetch === 'function') {
        photoController = typeof root.AbortController === 'function' ? new root.AbortController() : null;
        const activeController = photoController;
        const timeout = root.setTimeout?.(() => activeController?.abort(), 5000);
        void Promise.allSettled(hydrated.map((entry) => readProductPhoto(entry.candidate, entry.selection,
          { fetcher: root.window.fetch.bind(root.window), origin: root.location.origin, signal: activeController?.signal }).then((url) => {
          if (!url || latest !== hydrated || latestExpiresAt <= Date.now() || editingBrief) return;
          const card = Array.from(result.querySelectorAll?.('[data-discovery-idea]') || [])
            .find((node) => node.dataset.discoveryIdea === entry.candidate.candidateRef);
          const photo = card?.querySelector?.('[data-discovery-photo]');
          if (!photo) return;
          const fallback = card.querySelector('[data-discovery-photo-fallback]');
          const note = card.querySelector('[data-discovery-photo-note]');
          photo.onerror = () => { photo.hidden = true; if (fallback) fallback.hidden = false; if (note) note.hidden = true; };
          photo.onload = () => { photo.hidden = false; if (fallback) fallback.hidden = true; if (note) note.hidden = false; };
          photo.src = url;
        }))).finally(() => { if (timeout) root.clearTimeout?.(timeout); });
      }
      writeSummary(requestIntake, payload.understood);
      if (settle) {
        settleOperation();
      }
      submit.disabled = false; check.hidden = true;
      setStatus('', 'success');
      refreshIdeaEligibility({ announce: !restored });
      const renderedTiming = { ...timingContext, requestId: payload.requestId, draftRevision: payload.draftRevision, restored };
      observeTiming('discovery-rendered', hydrationStartedAt, renderedTiming);
      // Two frames give the committed suggestions a paint opportunity. Hidden
      // documents and superseded results must not claim visible suggestions.
      try {
        if (runtime?.environment === 'development' && runtime.themeId === '205891830103') root.requestAnimationFrame?.(() => {
          try { root.requestAnimationFrame?.(() => {
            try {
              if (!disposed && !departing && latest === hydrated && !result.hidden && root.document?.visibilityState !== 'hidden')
                observeTiming('discovery-visible', hydrationStartedAt, renderedTiming);
            } catch (_) { /* Optional visibility observation. */ }
          }); } catch (_) { /* Optional frame observation. */ }
        });
      } catch (_) { /* Rendering diagnostics do not own the result. */ }
      return true;
    };
    const activatePending = (restored, { restoreView = false } = {}) => {
      if (!restored || !coordinator.begin(restored.requestId)) return false;
      operation = restored; requestIntake = restored.intake;
      const pendingAnswers = validAnswers(restored.answers); const pendingWording = validWording(restored.wording);
      if (restoreView && pendingAnswers && pendingWording) restoreAnswers(pendingAnswers.answers, pendingWording);
      submit.disabled = true; check.hidden = false;
      setStatus('Your earlier request still needs a status check. It has not been sent again.', 'uncertain');
      return true;
    };
    const sourceEntryOwner = entry => {
      const submitted = Number.isSafeInteger(entry.guidedSubmittedAt) && entry.guidedSubmittedAt >= entry.createdAt
        && entry.guidedSubmittedAt <= Date.now() && entry.guidedSubmittedAt < entry.expiresAt
        && Number.isSafeInteger(entry.guidedSubmissionRevision) && entry.guidedSubmissionRevision >= 0 && entry.guidedSubmissionRevision <= entry.revision;
      return { revision: submitted ? entry.guidedSubmissionRevision : entry.revision,
        submittedAt: submitted ? entry.guidedSubmittedAt : entry.createdAt };
    };
    const applySourceEntry = entry => {
      retireChallenge();
      deferChallenge = false;
      form.elements.free_text.value = entry.rawText || '';
      entryFacts.sourceIntent = entry.descriptionOnly === true ? 'unknown' : ['upload', 'create', 'words'].includes(entry.sourceIntent) ? entry.sourceIntent : ['upload', 'create'].includes(entry.intent) ? entry.intent : 'unknown';
      entryFacts.photoPresent = entry.descriptionOnly !== true && entry.source?.recordId ? 'true' : 'false';
      entryFacts.selectedProductId = entry.descriptionOnly === true ? '' : entry.selection?.productId || ''; entryFacts.selectedVariantId = entry.descriptionOnly === true ? '' : entry.selection?.variantId || '';
      if (Array.isArray(entry.wording)) { selectedWording = validWording(entry.wording) || []; entryFacts.wordingText = selectedWording.join('\n'); entryFacts.wordingOwned = 'true'; }
      else if (typeof entry.wording === 'string') { entryFacts.wordingText = entry.wording; entryFacts.wordingOwned = 'true'; selectedWording = validWording([entry.wording]) || []; }
      if (Number.isSafeInteger(entry.createdAt) && Number.isSafeInteger(entry.revision)) {
        entryFacts.entryCreatedAt = String(entry.createdAt); entryFacts.entryRevision = String(sourceEntryOwner(entry).revision);
      }
      if (Number.isSafeInteger(entry.expiresAt)) briefExpiresAt = entry.expiresAt;
      retainedBriefState = briefState();
      projectView(); persistBrief();
    };
    const submittedBinding = entry => {
      const intent = entry?.guidedDispatch;
      if (entry?.intent !== 'guided' || intent?.version !== 1 || intent.status !== 'submitted'
        || !Number.isSafeInteger(intent.submissionRevision) || intent.submissionRevision !== entry.guidedSubmissionRevision
        || intent.submissionRevision !== entry.revision || intent.rawText !== entry.rawText
        || intent.sourceRecordId !== (entry.source?.recordId || '') || intent.sourceIntent !== (entry.sourceIntent || 'unknown')) return null;
      return { submissionRevision: intent.submissionRevision, rawText: intent.rawText, sourceRecordId: intent.sourceRecordId };
    };
    const bindingCurrent = async () => {
      if (!submittedGuided || disposed || departing || operation || latest || editingBrief || entryFacts.clarificationIssue
        || captureAnswers().freeText !== submittedGuided.rawText) return false;
      const retained = await root.DIMSourceIntake?.read?.();
      return !departing && !disposed && retained?.continuationStatus === 'ready' && identity(submittedBinding(retained.continuation)) === identity(submittedGuided);
    };
    const invalidateSubmission = ({ includeUnrestored = false } = {}) => {
      humanCheckStartedAt = null;
      preparedCatalogue = null;
      const cancelledPreparation = awaitingHumanCheck && Boolean(submittedGuided || manualSubmission);
      const previous = submittedGuided || submissionToCancel; submittedGuided = null; awaitingHumanCheck = false;
      manualSubmission = null;
      if (cancelledPreparation && !departing && !disposed && !operation)
        setStatus('Your idea has changed. Select Find suggestions when you are ready.', 'idle');
      if (!previous && !includeUnrestored) return Promise.resolve(true);
      submissionToCancel = previous;
      const cancelCurrent = current => {
        const owner = previous || submittedBinding(current?.continuation);
        if (owner && current?.continuationStatus === 'ready' && identity(submittedBinding(current.continuation)) === identity(owner)) {
          const saved = root.DIMSourceIntake?.stage?.({ guidedDispatch: { ...current.continuation.guidedDispatch, status: 'cancelled' } }, { expectedRevision: current.revision });
          if (saved?.guidedDispatch?.status !== 'cancelled') return false;
        }
        if (submissionToCancel === previous) submissionToCancel = null;
        return true; // A superseding owner already invalidated this instruction.
      };
      try {
        const current = root.DIMSourceIntake?.read?.();
        // The maintained storage owner is synchronous: commit its tombstone
        // during pagehide, before the document can be frozen or discarded.
        return current?.then ? Promise.resolve(current).then(cancelCurrent).catch(() => false) : Promise.resolve(cancelCurrent(current));
      } catch (_) { return Promise.resolve(false); }
    };
    const restoreSavedState = async () => {
      const expectedEpoch = restoreEpoch;
      if (!(await ensureRuntime())) return;
      const restoredPending = restoreOperation(readStored(recommendationKey()), { runtime, context, recordScope });
      if (activatePending(restoredPending, { restoreView: expectedEpoch === restoreEpoch })) return;
      const completedRaw = readStored(completedKey()); const nowMs = Date.now();
      const restored = restoreCompletedResult(completedRaw, { runtime, context, recordScope, nowMs });
      const savedBrief = restoreBriefRecord(readStored(briefKey()), { runtime, context, recordScope, nowMs });
      // A deliberate new Home entry outranks completed ideas, but never a
      // pending request. Keep its consumed revision in the existing answers so
      // reload preserves Discovery edits and the same entry cannot replace its
      // own later result. Older answer records remain readable above.
      if (ordinary) {
        let incoming;
        try { incoming = await root.DIMSourceIntake?.read?.(); } catch (_) { /* optional entry cannot grant authority */ }
        if (disposed || expectedEpoch !== restoreEpoch) return;
        const entry = incoming?.continuation;
        submittedGuided = incoming?.continuationStatus === 'ready' ? submittedBinding(entry) : null;
        if (incoming?.continuationStatus === 'ready' && entry?.intent === 'guided'
          && Number.isSafeInteger(entry.createdAt) && entry.createdAt >= 0 && entry.createdAt <= nowMs
          && Number.isSafeInteger(entry.revision) && entry.revision >= 0
          && Number.isSafeInteger(entry.expiresAt) && entry.expiresAt > nowMs
          && entry.expiresAt - entry.createdAt <= COMPLETED_RESULT_TTL_MS) {
          const owner = sourceEntryOwner(entry);
          if (submittedGuided) timingContext = { journeyStartedAt: owner.submittedAt, submittedAt: owner.submittedAt,
            entryCreatedAt: entry.createdAt, submissionRevision: owner.revision };
          const matchesEntry = answers => answers?.entryCreatedAt === String(entry.createdAt) && answers?.entryRevision === String(owner.revision);
          const prior = savedBrief?.answers || restored?.answers;
          const consumed = prior?.entryCreatedAt !== undefined && prior.entryCreatedAt !== '' && prior?.entryRevision !== undefined && prior.entryRevision !== '';
          const later = consumed ? entry.createdAt > Number(prior.entryCreatedAt)
            || entry.createdAt === Number(prior.entryCreatedAt) && owner.revision > Number(prior.entryRevision)
            : owner.submittedAt >= (restored?.createdAt || savedBrief?.createdAt || 0);
          if (matchesEntry(savedBrief?.answers) && !matchesEntry(restored?.answers)) {
            briefExpiresAt = savedBrief.expiresAt;
            restoreAnswers(savedBrief.answers, savedBrief.wording); projectView(); return;
          }
          if (later && !matchesEntry(restored?.answers)
            && (restored || savedBrief || !trimOrNull(form.elements.free_text.value))) {
            applySourceEntry(entry); return;
          }
        }
      }
      const restoreBrief = () => {
        if (expectedEpoch !== restoreEpoch) return;
        const brief = restoreBriefRecord(readStored(briefKey()), { runtime, context, recordScope, nowMs });
        if (!brief || (latest && brief.expiresAt !== latestExpiresAt)) return;
        briefExpiresAt = brief.expiresAt;
        restoreAnswers(brief.answers, brief.wording);
        editingBrief = Boolean(latest && (brief.editing || !answersMatchIdeas()));
        projectView(); refreshIdeaEligibility();
      };
      if (!restored) {
        const stored = safeJson(completedRaw);
        if (stored?.schemaVersion === 'dim-recommendation-completed@1' && Number.isSafeInteger(stored.expiresAt) && stored.expiresAt <= nowMs) {
          removeCompleted(); expiredView = true;
          setStatus('Those earlier suggestions have expired. Find suggestions to request new ones.', 'error');
        }
        restoreBrief(); return;
      }
      if (expectedEpoch !== restoreEpoch) return;
      const previousCandidates = candidates; const previousIntake = requestIntake;
      candidates = restored.candidates; requestIntake = restored.intake;
      try {
        if (!(await hydrateResult(restored.result, { expectedPlannerRequestId: restored.result.requestId, settle: false, retain: false, restored: true,
          submittedAnswers: restored.answers,
          submittedWording: restored.wording,
          expiresAt: restored.expiresAt, journeyAuthorityReference: restored.journeyAuthority?.reference || null,
          authorityGeneration: restored.authorityGeneration ?? null }))) throw new Error('INVALID_COMPLETED_RESULT');
        restoreAnswers(restored.answers, restored.wording);
        restoreBrief();
        projectView();
        refreshIdeaEligibility();
      } catch (_) {
        candidates = previousCandidates; requestIntake = previousIntake;
      }
    };
    const dispatchRequest = async () => {
      if (departing || disposed) return;
      let intake;
      if (!ordinary && modeValue() !== 'guided') { showValidation({ message: 'Choose who you are making this for.', field: 'purpose' }); return; }
      if (ordinary && Number(entryFacts.clarificationAttempts) >= 2) return;
      const issue = validateDiscoveryStep(null, captureAnswers());
      if (issue) { showValidation(issue); return; }
      try { intake = currentIntake(); } catch (error) { setStatus(messageFor(error), 'error'); return; }
      const originalAnswers = captureAnswers(); const originalWording = captureWording();
      if (!runtimeConfigured()) return;
      const guidedOwner = submittedGuided; const manualOwner = manualSubmission;
      const preparationEpoch = restoreEpoch; const preparationBrief = briefState();
      const preparationCurrent = () => !disposed && !departing && !operation && restoreEpoch === preparationEpoch
        && briefState() === preparationBrief && (guidedOwner ? submittedGuided === guidedOwner : manualOwner !== null && manualSubmission === manualOwner);
      const waitForHumanCheck = () => {
        if (humanCheckStartedAt === null) { humanCheckStartedAt = Date.now(); markTiming('discovery-human-check-wait', humanCheckStartedAt); }
        awaitingHumanCheck = Boolean(submittedGuided || manualSubmission); setStatus('Preparing your suggestions. Complete Human Check when it appears; your submitted idea will continue once it is ready.', 'loading');
      };
      if (!turnstileToken()) waitForHumanCheck();
      // The existing dispatch owner serialises callbacks. Only non-paid runtime
      // and catalogue preparation overlap Human Check; issue still needs proof.
      if (!(await ensureRuntime(preparationCurrent))) { if (preparationCurrent()) awaitingHumanCheck = false; return; }
      if (ordinary && modeValue() === 'free_text' && !ordinaryCapability(runtimeGlobal)) {
        awaitingHumanCheck = false;
        setStatus('This service does not yet support the new idea entry. Your description is retained; choose a direct route below.', 'blocked'); return;
      }
      if (activatePending(restoreOperation(readStored(recommendationKey()), { runtime, context, recordScope }))) return;
      const route = usesOriginalPhoto(intake) ? 'upload' : ['upload', 'words'].includes(intake.sourceIntent) ? intake.sourceIntent : 'create';
      if (!preparedCatalogue || preparedCatalogue.epoch !== preparationEpoch || preparedCatalogue.brief !== preparationBrief
        || preparedCatalogue.guidedOwner !== guidedOwner || preparedCatalogue.manualOwner !== manualOwner || preparedCatalogue.route !== route
        || preparedCatalogue.runtimeKey !== identity(runtime) || !sameContext(preparedCatalogue.context, context)) {
        preparedCatalogue = null;
        try {
          const loaded = await loadCandidates(route, preparationCurrent);
          if (!loaded || !preparationCurrent()) return;
          preparedCatalogue = { epoch: preparationEpoch, brief: preparationBrief, guidedOwner, manualOwner, route, context, runtimeKey: identity(runtime) };
        } catch (_) {
          if (preparationCurrent()) { awaitingHumanCheck = false; setStatus('The current products are unavailable, so we cannot suggest an idea yet.', 'blocked'); }
          return;
        }
      }
      if (!preparationCurrent()) return;
      if (activatePending(restoreOperation(readStored(recommendationKey()), { runtime, context, recordScope }))) return;
      if (!turnstileToken()) { waitForHumanCheck(); return; }
      if (humanCheckStartedAt !== null) { markTiming('discovery-human-check-ready', humanCheckStartedAt); humanCheckStartedAt = null; }
      awaitingHumanCheck = false;
      preparedCatalogue = null;
      const local = preflight({ intake, candidates });
      if (local.status === 'clarification') {
        focusCorrectionField = 'budget';
        if (ordinary) { entryFacts.clarificationIssue = 'budget'; entryFacts.clarificationQuestion = local.message;
          entryFacts.clarificationAttempts = '0'; entryFacts.clarificationBrief = normalizedBrief(form.elements.free_text.value); }
        setStatus(local.message, 'clarification'); return;
      }
      if (departing || disposed) return;
      submit.disabled = true;
      setStatus('', 'loading');
      const requireUnchangedBrief = () => {
        const currentAnswers = captureAnswers();
        if (disposed || departing || validateDiscoveryStep(null, currentAnswers) || currentAnswers.budget !== originalAnswers.budget
          || recommendationAnswersKey(currentAnswers) !== recommendationAnswersKey(originalAnswers)) throw new Error('BRIEF_CHANGED');
      };
      try {
        requireUnchangedBrief();
        requestIntake = intake;
        if (submittedGuided && !(await bindingCurrent())) { invalidateSubmission(); throw new Error('BRIEF_CHANGED'); }
        const savedDraft = runtimeGlobal.scene?.read?.().payload;
        const plannerRequestId = requestId();
        timingContext = { ...timingContext, requestId: plannerRequestId,
          draftRevision: Number.isSafeInteger(savedDraft?.draftRevision) ? savedDraft.draftRevision : 0 };
        markTiming('discovery-request-context', timingContext.journeyStartedAt);
        const input = JSON.stringify(createRecommendationInput({ intake, context, requestId: plannerRequestId,
          draftRevision: Number.isSafeInteger(savedDraft?.draftRevision) ? savedDraft.draftRevision : 0 }));
        const issueStartedAt = Date.now(); markTiming('discovery-issue-start', issueStartedAt);
        issueInFlight = true;
        const issued = await runtimeGlobal.authenticatedFetch(config.issuePath, { method: 'POST', headers: { 'content-type': 'application/json' }, body: input });
        const issuedPayload = await issued.json();
        issueInFlight = false;
        markTiming('discovery-issue-end', issueStartedAt);
        requireUnchangedBrief();
        if (!issued.ok && refreshableAuthority(issuedPayload?.error?.code || issuedPayload?.code)) {
          const refreshed = await refreshCurrentAuthority(issuedPayload?.error?.code || issuedPayload?.code, preparationCurrent);
          if (preparationCurrent()) setStatus(refreshed
            ? 'The available products were refreshed. Your answers and earlier suggestions are retained. Select Find suggestions when you are ready.'
            : 'The available products could not be refreshed. Your answers and earlier suggestions are retained. Reload this page to continue.', 'blocked');
          return;
        }
        if (!issued.ok || issuedPayload.status !== 'issued' || !issuedPayload.requestToken) throw new Error('REQUEST_UNCERTAIN');
        // The widget can expire or renew while issue is in flight. Use its CURRENT single-use token.
        const consumedToken = challenge?.take();
        if (!coordinator.begin(issuedPayload.requestToken)) throw new Error('REQUEST_UNCERTAIN');
        operation = createOperation({ runtime, context, requestId: issuedPayload.requestToken, plannerRequestId, intake,
          answers: originalAnswers, wording: originalWording, journeyAuthority: runtimeGlobal.journeyAuthority,
          authorityGeneration: runtimeGlobal.authorityGeneration });
        persistOperation();
        manualSubmission = null; awaitingHumanCheck = false;
        // A replacement owns its pending record; prior committed suggestions
        // remain recoverable until a validated replacement is committed.
        locallyUnsent = !consumedToken;
        if (locallyUnsent) {
          check.hidden = false;
          setStatus('Your answers have not been sent for suggestions. We are checking the issued request before you try the security check again.', 'uncertain');
          await poll();
          if (!operation) setStatus(`Complete the security check again, then select ${latest ? 'Find new suggestions' : 'Find suggestions'}. Your answers were not sent for suggestions.`, 'blocked');
          return;
        }
        if (submittedGuided) {
          const current = await root.DIMSourceIntake?.read?.();
          requireUnchangedBrief();
          if (!current || !await root.DIMSourceIntake?.consumeGuidedSubmission?.({ ...submittedGuided, expectedRevision: current.revision })) {
            locallyUnsent = true;
            setStatus('Your submitted idea changed before it was sent. Check its status before requesting more suggestions.', 'uncertain');
            await poll(); return;
          }
          submittedGuided = null; awaitingHumanCheck = false;
        }
        requireUnchangedBrief();
        const dispatchStartedAt = Date.now(); markTiming('discovery-dispatch-start', dispatchStartedAt);
        const response = await runtimeGlobal.authenticatedFetch(config.planningPath, { method: 'POST', headers: { 'content-type': 'application/json', 'X-DIM-Request-Token': operation.requestId, 'X-DIM-Turnstile-Token': consumedToken }, body: input });
        let serverTiming;
        try { serverTiming = response.headers?.get?.('Server-Timing'); } catch (_) { /* Optional diagnostics. */ }
        markTiming('discovery-response', dispatchStartedAt, { serverTiming });
        const payload = await response.json();
        timingContext.responseParsedAt = Date.now();
        markTiming('discovery-parsed', dispatchStartedAt);
        const outcome = classifyRequestOutcome(response, payload, operation.requestId);
        if (outcome.status === 'pending' || outcome.status === 'uncertain') throw new Error('REQUEST_UNCERTAIN');
        if (outcome.status === 'not_dispatched' || outcome.status === 'rejected') {
          coordinator.finish(operation.requestId, 'completed'); removeOperation(); operation = null;
          submit.disabled = false; check.hidden = true;
          if (outcome.code === 'BUDGET_CLARIFICATION_REQUIRED') {
            focusCorrectionField = 'budget';
            if (ordinary) { entryFacts.clarificationIssue = 'budget'; entryFacts.clarificationAttempts = '0'; entryFacts.clarificationQuestion = rejectionMessage(outcome.code); }
          }
          if (await refreshCurrentAuthority(outcome.code)) {
            setStatus('The available products were refreshed. Your answers and earlier suggestions are retained. Select Find suggestions when you are ready.', 'blocked'); return;
          }
          setStatus(outcome.code === 'TURNSTILE_INVALID' ? `The security check expired or was not accepted. Complete it again, then select ${latest ? 'Find new suggestions' : 'Find suggestions'}. Your request was not sent for suggestions.` : rejectionMessage(outcome.code), 'error');
          return;
        }
        if (outcome.result === null) {
          settleOperation();
          submit.disabled = false; check.hidden = true;
          unavailableView = true;
          setStatus(unavailableIdeasMessage, 'error');
          return;
        }
        if (!(await hydrateResult(outcome.result))) throw new Error('INVALID_RESPONSE');
      } catch (error) {
        const code = ['INVALID_RESPONSE', 'REQUEST_UNCERTAIN', 'PENDING_UNAVAILABLE'].includes(error?.message) ? error.message : 'REQUEST_FAILED';
        section.dataset.discoveryFailure = code;
        root.console?.warn?.('Dream it recommendation could not be displayed', { code });
        if (!operation) {
          setStatus(error?.message === 'BRIEF_CHANGED' ? 'Your answers changed before the request was sent. Check them, then select Find suggestions.' : 'We could not start that request. Your answers are still here.', 'error');
          check.hidden = true;
          return;
        }
        coordinator.finish(operation.requestId, 'uncertain');
        check.hidden = false;
        setStatus('We do not know whether that request completed. Check its status before starting another request.', 'uncertain');
      } finally { issueInFlight = false; submit.disabled = coordinator.state().status !== 'idle'; }
    };
    const dispatch = async () => {
      if (dispatchInFlight || departing || disposed) return;
      if (!timingContext.journeyStartedAt || timingContext.requestId) timingContext = { journeyStartedAt: Date.now() };
      markTiming('discovery-preparation-start', timingContext.journeyStartedAt);
      dispatchInFlight = true;
      focusCorrectionField = null;
      if (announcer) announcer.textContent = '';
      projectView({ focus: true });
      initiatedFocus = root.document?.activeElement === pendingHeading;
      refreshIdeaEligibility();
      try {
        if (initialRestore) await initialRestore;
        if (latest && !editingBrief && latestExpiresAt > Date.now()) return;
        if (operation || coordinator.state().status !== 'idle') {
          submit.disabled = true; check.hidden = false;
          setStatus('Your earlier request still needs a status check. It has not been sent again.', 'uncertain');
          return;
        }
        await dispatchRequest();
      } finally {
        const owned = initiatedFocus && root.document?.activeElement === pendingHeading;
        dispatchInFlight = false; projectView(); refreshIdeaEligibility();
        if (ordinary && !operation && (!latest || status.dataset.state === 'clarification')) persistBrief();
        if (focusCorrectionField && !form.hidden) {
          if (focusCorrectionField === 'budget' && budgetDisclosure) budgetDisclosure.open = true;
          form.elements[focusCorrectionField]?.focus?.();
        }
        focusCorrectionField = null;
        if (awaitingHumanCheck && challenge?.peek()) void Promise.resolve().then(resumeSubmitted);
      }
    };
    resumeSubmitted = async () => {
      if (dispatchInFlight || !submittedGuided && !manualSubmission || disposed || departing) return;
      if (submittedGuided ? !(await bindingCurrent()) : manualSubmission !== briefState()) return;
      if (dispatchInFlight || disposed || departing) return;
      awaitingHumanCheck = false;
      await dispatch();
    };
    const poll = async () => {
      if (!operation || !(await ensureRuntime()) || !canCommitOperation(operation, { runtime, context, requestId: operation.requestId })) return;
      check.disabled = true;
      try {
        const response = await runtimeGlobal.authenticatedFetch(config.pollPath, { method: 'GET', headers: { 'X-DIM-Request-Token': operation.requestId } });
        const payload = await response.json();
        const outcome = classifyRequestOutcome(response, payload, operation.requestId);
        if (outcome.status === 'pending') { setStatus(locallyUnsent ? 'Your answers have not been sent for suggestions. Check the issued request status before starting another request.' : 'That request is still pending. It has not been sent again.', 'uncertain'); return; }
        if (outcome.status === 'completed') {
          if (outcome.result === null) {
            settleOperation();
            submit.disabled = false; check.hidden = true;
            unavailableView = true;
            setStatus(unavailableIdeasMessage, 'error');
            return;
          }
          if (outcome.result.status === 'ideas') await loadCandidates(['upload', 'words'].includes(outcome.result.understood?.sourceIntent) ? outcome.result.understood.sourceIntent : 'create');
          if (!(await hydrateResult(outcome.result))) throw new Error('INVALID_RESPONSE');
          check.hidden = true;
          return;
        }
        if (outcome.status === 'not_dispatched' || outcome.status === 'rejected') {
          coordinator.finish(operation.requestId, 'completed'); removeOperation(); operation = null; submit.disabled = false; check.hidden = true;
          if (await refreshCurrentAuthority(outcome.code)) {
            setStatus('The available products were refreshed. Your answers and earlier suggestions are retained. Select Find suggestions when you are ready.', 'blocked'); return;
          }
          setStatus(rejectionMessage(outcome.code), 'error');
          return;
        }
        setStatus(locallyUnsent ? 'Your answers have not been sent for suggestions. Check the issued request status before starting another request.' : 'We still cannot confirm that request. It has not been sent again.', 'uncertain');
      } catch (_) { setStatus('We still cannot confirm that request. It has not been sent again.', 'uncertain'); }
      finally { check.disabled = false; }
    };
    let handoffInFlight = false;
    const ideasAreCurrent = () => {
      if (departing || disposed) return false;
      if (operation || dispatchInFlight) return false;
      if (Number.isSafeInteger(latestAuthorityGeneration) ? latestAuthorityGeneration !== runtimeGlobal.authorityGeneration
        : latestAuthority && latestAuthority !== runtimeGlobal.journeyAuthority?.reference) {
        setStatus('The available products have changed. Your earlier suggestions and answers are retained. Select Find suggestions to use the current products.', 'blocked');
        return false;
      }
      if (!latest || !Number.isSafeInteger(latestExpiresAt) || latestExpiresAt <= Date.now()) {
        latest = null; latestExpiresAt = null; latestAnswers = null; latestWording = []; editingBrief = false; expiredView = true; removeCompleted();
        setStatus('Those earlier suggestions have expired. Find suggestions to request new ones.', 'error');
        return false;
      }
      if (editingBrief) return false;
      if (!answersMatchIdeas()) { refreshIdeaEligibility({ announce: true }); return false; }
      return true;
    };
    const useIdea = async (candidateRef, variantId, selectedChoices = null, letteringChoice = null, button = null, actionKind = 'generate') => {
      if (!['personalise', 'generate', 'upload'].includes(actionKind)) return;
      if (handoffInFlight || !ideasAreCurrent()) return;
      const initial = latest && latest.find((value) => value.candidate.candidateRef === candidateRef);
      const selection = initial?.selection || exactSelectionForChoices(initial?.candidate, selectedChoices);
      const entry = initial && selection && (variantId === '' || String(selection.variantId) === String(variantId)) ? { ...initial, selection } : null;
      const card = button?.closest?.('[data-discovery-idea]');
      const selectionStillCurrent = () => initial?.selection || identity(exactSelectionForChoices(initial?.candidate,
        Object.fromEntries(Array.from(card?.querySelectorAll?.('[data-discovery-option]') || []).map(field => [field.dataset.discoveryOption, field.value])))) === identity(selection);
      const cardStatus = card?.querySelector?.('[data-discovery-card-status]');
      const cardMessage = (message, state = 'error') => {
        if (cardStatus) { delete cardStatus.dataset.validationOwner; cardStatus.textContent = message; cardStatus.hidden = !message; cardStatus.dataset.state = state; }
        else setStatus(message, state);
      };
      if (!entry) {
        const missing = Array.from(card?.querySelectorAll?.('[data-discovery-option]') || []).filter(field => !field.value);
        missing.forEach(field => field.setAttribute?.('aria-invalid', 'true'));
        cardMessage(`Choose ${missing.map(field => field.dataset.discoveryOption).join(' and ') || 'each available product option'} for ${initial?.product.title || 'this idea'} to continue.`, 'clarification');
        missing[0]?.focus?.(); return;
      }
      if (actionKind === 'personalise') {
        if (typeof root.DIMSourceIntake?.continue !== 'function') { cardMessage('Personalisation is unavailable until the current service supports this continuation. Your idea is retained.'); return; }
        handoffInFlight = true;
        try {
          persistBrief(); persistCurrentWording();
          const sourceIntent = usesOriginalPhoto(requestIntake) ? 'upload' : entryFacts.sourceIntent === 'unknown' ? latestUnderstood?.sourceIntent || 'unknown' : entryFacts.sourceIntent;
          await root.DIMSourceIntake.continue({ intent: 'personalise', rawText: form.elements.free_text.value,
            sourceIntent, selection: entry.selection, idea: entry.idea,
            ...(confirmedIssues(entryFacts.confirmedClarifications).includes('source') ? { sourceIntentConfirmed: true } : {}),
            ...(independentWording() !== null ? { wording: independentWording().join('\n') } : {}), suggestedWording: card?.querySelector?.('[data-discovery-wording]')?.value || '' });
          const destination = new URL(config.createUrl, root.location.origin);
          destination.searchParams.set('entry', 'source'); destination.searchParams.set('product', entry.selection.productId);
          destination.searchParams.set('variant', entry.selection.variantId);
          if (['upload', 'create', 'words'].includes(sourceIntent)) destination.searchParams.set('route', sourceIntent);
          await root.DreamItJourneyRoutes.navigateJourney({ url: destination.href, runtime: runtimeGlobal, location: root.location,
            assign: root.location.assign.bind(root.location), family: 'guided', isCurrent: ideasAreCurrent });
        } catch (_) { cardMessage('This product could not be opened. Your brief and design are retained.'); }
        finally { handoffInFlight = false; }
        return;
      }
      let corrected;
      let generatedDescription = null;
      const descriptionCurrent = () => generatedDescription === null || trimOrNull(card?.querySelector?.('[data-discovery-new-image]')?.value) === generatedDescription;
      try {
        const newImageField = card?.querySelector?.('[data-discovery-new-image]');
        const newImageDescription = trimOrNull(newImageField?.value);
        if (usesOriginalPhoto(requestIntake) && actionKind === 'generate' && (!newImageDescription || graphemes(newImageDescription) > 1000)) {
          newImageField?.setAttribute?.('aria-invalid', 'true');
          if (newImageField?.dataset) newImageField.dataset.discoveryValidation = 'new-image';
          newImageField?.focus?.();
          cardMessage('Describe the new image you want to generate, in up to 1000 characters. Your attached photo will not be used or edited.', 'clarification');
          if (cardStatus) cardStatus.dataset.validationOwner = 'new-image';
          return;
        }
        const idea = usesOriginalPhoto(requestIntake) && actionKind === 'generate' ? { ...entry.idea,
          artworkIntent: { subject: newImageDescription,
            suggestedStyle: entry.idea.artworkIntent.suggestedStyle, compositionIntent: 'Create a new image without using or editing the attached photo.', letteringMode: 'none', wordingSuggestion: null } } : entry.idea;
        if (usesOriginalPhoto(requestIntake) && actionKind === 'generate') generatedDescription = newImageDescription;
        corrected = resolveLettering(idea, letteringChoice);
      }
      catch (error) {
        cardMessage(error.message === 'LETTERING_DESCRIPTION_REQUIRED' ? 'Change the image description before keeping the wording editable.' : error.message === 'LETTERING_CHOICE_REQUIRED' ? 'Edit the image description and confirm that the wording stays editable.' : 'This idea cannot be prepared yet.', 'clarification');
        card?.querySelector?.('[data-discovery-lettering-subject]')?.focus?.();
        return;
      }
      handoffInFlight = true;
      const buttonCopy = button?.textContent;
      if (button) { button.disabled = true; button.setAttribute?.('aria-busy', 'true'); button.textContent = 'Preparing this suggestion…'; }
      cardMessage(actionKind === 'upload' ? 'Checking your selected product and preparing Upload it…' : 'Checking your selected product and preparing your image…', 'loading');
      try {
        const admitted = await runtimeGlobal.requireAdmission({ ...entry.selection, route: actionKind === 'upload' ? 'upload' : 'create' });
        if (!descriptionCurrent()) throw new Error('NEW_IMAGE_CHANGED');
        if (!ideasAreCurrent()) return;
        if (!selectionStillCurrent()) throw new Error('STALE_SELECTION');
        if (!admitted?.selection || !sameContext(admitted.context, context)) throw new Error('STALE_SELECTION');
        const currentSelection = selectExactCandidate([entry.candidate], { candidateRef: entry.candidate.candidateRef, selection: admitted.selection });
        if (!currentSelection) throw new Error('STALE_SELECTION');
        const wordingField = card?.querySelector?.('[data-discovery-wording]');
        const wording = independentWording() !== null ? independentWording() : wordingField?.value?.trim() ? [wordingField.value.trim()] : [];
        const savedDraft = runtimeGlobal.scene?.read?.().payload;
        const handoff = createHandoff({ runtime, context: admitted.context, draftRevision: Number.isSafeInteger(savedDraft?.draftRevision) ? savedDraft.draftRevision : 0,
          origin: root.location.origin, selection: currentSelection, idea: corrected.idea, wording, letteringResolution: corrected.letteringResolution, fontCatalogue: runtimeGlobal.fontCatalogue,
          validateLayers: root.DIMTextModel?.validateLayers, action: { id: requestId(), kind: actionKind } });
        if (root.DIMGuidedHandoff?.validate) {
          root.DIMGuidedHandoff.validate(handoff, { draftRevision: handoff.draftRevision, origin: handoff.origin, context, runtime: handoff.runtime, nowMs: Date.now() }, () => true, { textModel: root.DIMTextModel });
        }
        if (!ideasAreCurrent()) return;
        try {
          root.console?.debug?.('Dream it handoff wording', {
            canonicalWordingPresent: typeof latestUnderstood?.wording === 'string' && Boolean(latestUnderstood.wording.trim()),
            explicitOwned: entryFacts.wordingOwned === 'true' || captureWording().length > 0,
            ownedLineCount: captureWording().length, resolvedLineCount: wording.length, layerCount: handoff.textLayers.length
          });
        } catch (_) { /* Optional metadata diagnostics cannot affect the handoff. */ }
        persistCurrentWording();
        // Keep accepted/customer wording (including an intentional clear) in
        // the existing continuation owner. Optional model suggestions confer
        // no wording authority over an already accepted editor design.
        if (independentWording() !== null && typeof root.DIMSourceIntake?.stage === 'function') {
          const currentSource = await root.DIMSourceIntake.read();
          if (!ideasAreCurrent() || !descriptionCurrent() || departing || disposed) return;
          await root.DIMSourceIntake.stage({ wording: wording.join('\n') }, { expectedRevision: currentSource.revision });
        }
        if (!descriptionCurrent()) throw new Error('NEW_IMAGE_CHANGED');
        if (!selectionStillCurrent()) throw new Error('STALE_SELECTION');
        const studio = root.document.querySelector('[data-dream-studio]');
        if (studio) {
          studio.dispatchEvent(new CustomEvent('dim:guided-design-pending', { bubbles: true, cancelable: true, detail: handoff }));
          setStatus('', 'idle');
          return;
        }
        const pendingKey = runtimeGlobal.scene?.keys?.pending;
        if (!pendingKey) throw new Error('PENDING_UNAVAILABLE');
        const destination = new URL(config.createUrl, root.location.origin);
        destination.searchParams.set('view', 'dim-create');
        destination.searchParams.set('guided', '1');
        destination.searchParams.set('route', actionKind === 'upload' ? 'upload' : 'create');
        destination.searchParams.set('product', currentSelection.productId);
        destination.searchParams.set('variant', currentSelection.variantId);
        await runtimeGlobal.prepareTransition(destination.pathname);
        if (!descriptionCurrent()) throw new Error('NEW_IMAGE_CHANGED');
        if (!ideasAreCurrent()) return;
        if (JSON.stringify(independentWording() !== null ? independentWording() : wordingField?.value?.trim() ? [wordingField.value.trim()] : []) !== JSON.stringify(wording)) throw new Error('WORDING_CHANGED');
        root.sessionStorage.setItem(pendingKey, JSON.stringify(handoff));
        if (root.sessionStorage.getItem(pendingKey) !== JSON.stringify(handoff)) throw new Error('PENDING_UNAVAILABLE');
        root.location.assign(destination.href);
      } catch (error) {
        if (refreshableAuthority(error?.code || error?.message)) {
          const refreshed = await refreshCurrentAuthority(error.code || error.message,
            () => !disposed && !departing && selectionStillCurrent() && descriptionCurrent());
          if (!disposed && !departing) cardMessage(refreshed
            ? 'The available products were refreshed. Your idea and wording are retained. Edit your idea to find current suggestions.'
            : 'The available products could not be refreshed. Your idea and wording are retained. Reload this page to continue.');
          return;
        }
        if (!ideasAreCurrent()) return;
        cardMessage(error.message === 'WORDING_CHANGED' ? 'Your wording changed while this idea was being prepared. Choose your image action again to keep your latest wording.'
          : error.message === 'NEW_IMAGE_CHANGED' ? 'Your new image description changed. Select Generate an Image with AI again when it is ready.'
          : error.message === 'INVALID_WORDING' ? 'Use up to three wording lines, each no more than 120 characters.' : error.message === 'STALE_SELECTION' ? 'That product option has changed. Choose an available option before continuing.' : 'This idea cannot be prepared yet.', 'error');
      }
      finally { handoffInFlight = false; if (button) { button.disabled = false; button.removeAttribute?.('aria-busy'); button.textContent = buttonCopy; } refreshIdeaEligibility(); }
    };
    form.addEventListener('change', () => { if (challenge && challengeBrief !== briefState()) { retireChallenge(); deferChallenge = true; } invalidateSubmission(); restoreEpoch += 1; projectView(); refreshIdeaEligibility({ announce: true }); persistBrief(); });
    form.addEventListener('input', (event) => {
      if (challenge && challengeBrief !== briefState()) { retireChallenge(); deferChallenge = true; }
      invalidateSubmission();
      restoreEpoch += 1;
      if (!ordinary) clearValidation();
      else if (validation?.dataset.code === 'INVALID_TEXT' && trimOrNull(form.elements.free_text.value)) clearValidation();
      else if (validation?.dataset.code === 'CLARIFICATION_REQUIRED' && event?.target === clarificationAnswer && trimOrNull(clarificationAnswer.value)) clearValidation();
      if (ordinary && event?.target === form.elements.free_text && normalizedBrief(form.elements.free_text.value) !== entryFacts.clarificationBrief) {
        entryFacts.clarificationIssue = ''; entryFacts.clarificationAttempts = '0'; entryFacts.clarificationQuestion = '';
        entryFacts.confirmedClarifications = '';
        if (!operation && !dispatchInFlight) submit.disabled = false;
        if (status.dataset.state === 'clarification') setStatus('Your idea has changed. Select Find suggestions when you are ready.', 'idle');
      }
      if (event?.target === form.elements.recipient_custom) form.elements.recipient.value = 'someone-else';
      projectView(); refreshIdeaEligibility({ announce: true });
      persistBrief();
      if (event?.target === form.elements.budget) {
        let valid = true; try { wholePoundEntry(form.elements.budget.value); } catch (_) { valid = false; }
        form.elements.budget.setAttribute?.('aria-invalid', String(!valid));
        if (!valid && validation) { validation.textContent = 'Enter a whole number, such as 25.'; validation.hidden = false; }
      }
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (form.hidden) return;
      departing = false; // A fresh deliberate action can recover a failed navigation.
      if (ordinary && Number(entryFacts.clarificationAttempts) >= 2) return;
      if (!ordinary && modeValue() !== 'guided') {
        modeField.value = 'guided'; form.elements.purpose.value = '';
        projectView();
      }
      const issue = validateDiscoveryStep(null, captureAnswers());
      if (issue) { showValidation(issue); return; }
      if (ordinary && entryFacts.clarificationIssue) {
        const correctionIssue = entryFacts.clarificationIssue;
        const structured = ['source', 'purpose', 'product'].includes(correctionIssue);
        const answer = correctionIssue === 'budget' ? String(form.elements.budget.value).trim() : trimOrNull(structured ? clarificationChoice?.value : clarificationAnswer?.value);
        if (!answer && correctionIssue !== 'budget' && correctionIssue !== 'wording') { showValidation({ code: 'CLARIFICATION_REQUIRED', message: 'Add an answer or choose a direct route below.', field: structured ? 'clarification_choice' : 'clarification_answer' }); return; }
        if (correctionIssue === 'source' && !['upload', 'create', 'words'].includes(answer)
          || correctionIssue === 'purpose' && !['self', 'gift'].includes(answer)) return;
        if (correctionIssue === 'product') {
          const selection = candidates.flatMap(candidate => candidate.allowedSelections).find(value => `${value.productId}:${value.variantId}` === answer);
          if (!selection) { showValidation({ code: 'CLARIFICATION_REQUIRED', message: 'Choose a currently available product option.', field: 'clarification_choice' }); return; }
          entryFacts.selectedProductId = selection.productId; entryFacts.selectedVariantId = selection.variantId;
        }
        if (correctionIssue === 'source') entryFacts.sourceIntent = answer;
        if (correctionIssue === 'purpose') form.elements.purpose.value = answer;
        if (correctionIssue === 'wording') {
          if (graphemes(answer) > MAX_WORDING_GRAPHEMES) { showValidation({ code: 'INVALID_WORDING', message: 'Keep your wording within 120 characters.', field: 'clarification_answer' }); return; }
          entryFacts.wordingText = answer || ''; entryFacts.wordingOwned = 'true'; selectedWording = answer ? [answer] : [];
        }
        if (CORRECTION_ISSUES.includes(correctionIssue)) entryFacts.confirmedClarifications = [...new Set([...confirmedIssues(entryFacts.confirmedClarifications), correctionIssue])].join(',');
        const history = `${entryFacts.clarificationHistory}\n${entryFacts.clarificationQuestion}\n${answer}`.trim();
        if (graphemes(`${form.elements.free_text.value}\n${history}`) > MAX_FREE_TEXT) { showValidation({ code: 'INVALID_TEXT', message: 'Keep your idea and answers within 1,800 characters.', field: 'free_text' }); return; }
        entryFacts.clarificationHistory = history;
        clarificationAnswer.value = ''; persistBrief();
      }
      timingContext = { journeyStartedAt: Date.now() }; humanCheckStartedAt = null; preparedCatalogue = null;
      manualSubmission = briefState(); deferChallenge = false;
      section.__dimDiscoveryMountTurnstile?.();
      void dispatch();
    });
    const cancelBriefEdit = () => {
      if (!editingBrief || dispatchInFlight || operation) return;
      if (!latest || latestExpiresAt <= Date.now()) { ideasAreCurrent(); return; }
      restoreEpoch += 1;
      restoreAnswers(latestAnswers, latestWording);
      retireChallenge();
      editingBrief = false; clearValidation();
      removeStored(briefKey()); briefExpiresAt = latestExpiresAt;
      setStatus('', 'success');
      projectView({ focus: true });
      refreshIdeaEligibility();
    };
    cancelEdit?.addEventListener('click', cancelBriefEdit);
    cancelEditEarly?.addEventListener('click', cancelBriefEdit);
    check.addEventListener('click', poll);
    section.querySelectorAll?.('[data-discovery-direct]').forEach(control => control.addEventListener('click', async event => {
      if (typeof root.DIMSourceIntake?.continue !== 'function' || !root.DreamItJourneyRoutes?.isJourneyActivation?.(event, control)) return;
      event.preventDefault();
      departing = true;
      try {
        if (!(await invalidateSubmission({ includeUnrestored: true }))) throw new Error('SUBMISSION_CANCEL_FAILED');
        persistBrief();
        await root.DIMSourceIntake.continue({ intent: control.dataset.discoveryDirect, rawText: [form.elements.free_text.value, entryFacts.clarificationHistory].filter(Boolean).join('\n'), ...(entryFacts.wordingOwned === 'true' || captureWording().length ? { wording: captureWording().join('\n') } : {}) });
        await root.DreamItJourneyRoutes.navigateJourney({ url: control.href, runtime: runtimeGlobal, location: root.location,
          assign: root.location.assign.bind(root.location), family: control.dataset.discoveryDirect === 'choose' ? 'product' : 'source' });
      } catch (_) { departing = false; setStatus('This route could not be opened. Your idea and design are retained.', 'error'); }
    }));
    result.addEventListener('input', (event) => {
      const field = event.target?.closest?.('[data-discovery-new-image]');
      if (!field || field.dataset.discoveryValidation !== 'new-image' || !trimOrNull(field.value) || graphemes(field.value) > 1000) return;
      field.removeAttribute?.('aria-invalid'); delete field.dataset.discoveryValidation;
      const cardStatus = field.closest('[data-discovery-idea]')?.querySelector?.('[data-discovery-card-status]');
      if (cardStatus?.dataset.validationOwner === 'new-image') {
        delete cardStatus.dataset.validationOwner; cardStatus.dataset.state = 'idle'; cardStatus.textContent = ''; cardStatus.hidden = true;
      }
    });
    result.addEventListener('change', (event) => {
      const wordingField = event.target?.closest?.('[data-discovery-wording]');
      if (wordingField) { selectedWording = wordingField.value.trim() ? [wordingField.value.trim()] : []; entryFacts.wordingText = wordingField.value; entryFacts.wordingOwned = 'true'; persistCurrentWording(); persistBrief(); return; }
      const field = event.target?.closest?.('[data-discovery-option]');
      if (!field || !field.value) return;
      field.removeAttribute?.('aria-invalid');
      const cardStatus = field.closest('[data-discovery-idea]')?.querySelector?.('[data-discovery-card-status]');
      if (cardStatus?.dataset.state === 'clarification') { cardStatus.hidden = true; cardStatus.textContent = ''; }
      const card = field.closest('[data-discovery-idea]');
      const entry = latest?.find(value => value.candidate.candidateRef === card?.dataset.discoveryIdea);
      const choices = Object.fromEntries(Array.from(card?.querySelectorAll?.('[data-discovery-option]') || []).map(value => [value.dataset.discoveryOption, value.value]));
      const selection = exactSelectionForChoices(entry?.candidate, choices);
      const price = entry ? candidatePrice(entry.candidate, selection) : null;
      const priceNode = card?.querySelector?.('[data-discovery-price]'); if (priceNode && price) priceNode.textContent = price;
    });
    result.addEventListener('click', (event) => {
      const edit = event.target?.closest?.('[data-discovery-edit-brief]');
      if (edit) {
        if (disposed || departing || operation || dispatchInFlight) return;
        if (!latest || latestExpiresAt <= Date.now()) { ideasAreCurrent(); return; }
        editingBrief = true;
        deferChallenge = true;
        if (!ordinary && latestAnswers?.mode === 'free_text') {
          modeField.value = 'guided'; form.elements.purpose.value = '';
          form.elements.recipient.value = 'someone-else'; form.elements.recipient_custom.value = '';
          form.elements.interests.value = '';
        }
        projectView({ focus: true });
        setStatus('', 'idle');
        persistBrief();
        return;
      }
      const returnToIdeas = event.target.closest('[data-discovery-return-ideas]');
      if (returnToIdeas?.dataset?.discoveryReturnIdeas !== undefined) {
        event.preventDefault();
        const currentCard = returnToIdeas.closest('[data-discovery-idea]');
        const another = Array.from(result.querySelectorAll?.('[data-discovery-use]') || []).find((control) => control.closest('[data-discovery-idea]') !== currentCard);
        another?.focus?.();
        setStatus('', 'idle');
        return;
      }
      const button = event.target.closest('[data-discovery-use]');
      if (!button) return;
      const card = button.closest('[data-discovery-idea]');
      const choices = Object.fromEntries(Array.from(card?.querySelectorAll?.('[data-discovery-option]') || []).map((field) => [field.dataset.discoveryOption, field.value]));
      const letteringChoice = { subject: card?.querySelector?.('[data-discovery-lettering-subject]')?.value, compositionIntent: card?.querySelector?.('[data-discovery-lettering-composition]')?.value,
        confirmed: card?.querySelector?.('[data-discovery-lettering-confirm]')?.checked === true };
      useIdea(button.dataset.discoveryUse, button.dataset.discoveryVariant, choices, letteringChoice, button, button.dataset.discoveryAction || 'generate');
    });
    const onPageHide = () => { departing = true; retireChallenge(); void invalidateSubmission({ includeUnrestored: true }); persistBrief(); photoController?.abort?.(); };
    const onPageShow = () => {
      if (disposed) return;
      const returned = departing; departing = false;
      if (returned && !operation && !submittedGuided && !latest) setStatus('Your idea is retained. Select Find suggestions when you are ready.', 'idle');
      else projectView();
    };
    const flushForDeparture = async () => {
      await initialRestore;
      if (disposed) return true;
      if (!(await invalidateSubmission())) {
        setStatus('Your submitted idea could not be saved as cancelled. Stay here and try leaving again; your work is still on this page.', 'error');
        departing = false; return false;
      }
      const unsavedEdits = briefState() !== retainedBriefState;
      const currentIdeas = Boolean(latest && latestExpiresAt > Date.now());
      if (!operation && !dispatchInFlight && !unsavedEdits && !currentIdeas) return true;
      let saved = false;
      try {
        if (operation) { persistOperation(); saved = true; }
        else if (!issueInFlight) saved = persistBrief({ cancelledPreparation: true }) && persistCurrentWording();
      } catch (_) { /* The active request remains owned even if storage failed. */ }
      if (!saved) { departing = false; setStatus('Your latest idea could not be saved. Stay here and try again before leaving; your current work is still on this page.', 'error'); }
      return saved;
    };
    const onBeforeJourneyLeave = event => { departing = true; retireChallenge(); event.detail?.waitUntil?.(flushForDeparture()); };
    const onSectionUnload = event => { if (event.target === section || event.target?.contains?.(section)) section.__dimDiscoveryDispose?.(); };
    root.document?.addEventListener?.('dim:before-journey-leave', onBeforeJourneyLeave);
    root.document?.addEventListener?.('shopify:section:unload', onSectionUnload);
    root.addEventListener?.('pagehide', onPageHide);
    root.addEventListener?.('pageshow', onPageShow);
    section.__dimDiscoveryDispose = () => { disposed = true; challenge?.dispose(); if (expiryTimer) root.clearTimeout?.(expiryTimer);
      photoController?.abort?.(); root.removeEventListener?.('pagehide', onPageHide); root.removeEventListener?.('pageshow', onPageShow);
      root.document?.removeEventListener?.('dim:before-journey-leave', onBeforeJourneyLeave);
      root.document?.removeEventListener?.('shopify:section:unload', onSectionUnload);
      const widget = turnstile?.querySelector?.('.cf-turnstile');
      if (widget?.dataset) delete widget.dataset.discoveryTurnstileMounted;
      delete section.__dimDiscoveryMountTurnstile; };
    projectView();
    runtimeConfigured();
    initialRestore = restoreSavedState().finally(() => { restoringEntry = false; if (!disposed) projectView(); markTiming('discovery-restore-end', restoreStartedAt); });
    if (ordinary) void Promise.resolve(initialRestore).then(async () => {
      const retained = await root.DIMSourceIntake?.read?.();
      const entry = retained?.continuation;
      if (!entry || retained.continuationStatus !== 'ready' || entry.expiresAt !== undefined && entry.expiresAt <= Date.now()
        || disposed || operation || latest) return;
      if (!trimOrNull(form.elements.free_text.value)) applySourceEntry(entry);
      await resumeSubmitted();
    }).catch(() => { /* retained entry never grants request authority */ });
  }

  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const escapeAttr = escapeHtml;
  function boot() {
    if (!root || !root.document) return;
    root.document.querySelectorAll('[data-dim-discovery]').forEach(initialise);
  }
  if (root && root.document) root.document.addEventListener('DOMContentLoaded', boot, { once: true });

  const mountTurnstile = () => root?.document?.querySelectorAll?.('[data-dim-discovery]')
    .forEach((section) => section.__dimDiscoveryMountTurnstile?.());

  return Object.freeze({ ILLUSTRATION_INSTRUCTION, parseIntake, preflight, validateDiscoveryStep, minimumEligiblePrice, createChallengeController, createTimingObserver,
    createOperation, canCommitOperation, createRecommendationInput, restoreOperation,
    createCompletedResultRecord, restoreCompletedResult, createBriefRecord, restoreBriefRecord, readSuggestedWording, readProductPhoto,
    selectExactCandidate, constrainCandidates, wordsCandidates, optionChoices, exactSelectionForChoices, createCoordinator, projectPhotoRecommendation,
    classifyRequestOutcome, createHandoff, createWordingLayers, resolveLettering, wordingValues, resultPresentation, vagueBrief, clarificationProgress, ordinaryCapability, initialise, boot, mountTurnstile });
}));
